import { formatTime, formatDate, formatName } from '../lib/format';
import SectionCard from '../components/ui/SectionCard';
import PageContent from '../components/ui/PageContent';
import PageHeader from '../components/ui/PageHeader';
import FieldLabel from '../components/ui/FieldLabel';
import UiInput from '../components/ui/Input';
import DateInput from '../components/ui/DateInput';
import Textarea from '../components/ui/Textarea';
import UiSelect from '../components/ui/Select';
import UiCheckbox from '../components/ui/Checkbox';
import Button from '../components/ui/Button';
import Tabs from '../components/ui/Tabs';
import { withDownloadProgress } from '../utils/downloadProgress';
import { useEffect, useRef, useState } from 'react';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import Section from '../components/Section';
import MyCalendar from '../components/Calendar';
import CompactSuccessModal from '../components/CompactSuccessModal';
import { PlusIcon } from '../components/NavIcons';
import ModalComponent from '../components/Modal';
import SignatureCanvas from 'react-signature-canvas';
import { jsPDF } from 'jspdf';
import { useSelector } from 'react-redux';
import Dropdown from '../components/Dropdown';
import { postWithAuth } from '../common';
import { toast } from 'react-toastify';
import { uploadFileToStorage } from '../storageUpload';
import LoadingPopup from '../components/LoadingPopup';
import { useNavigate } from 'react-router-dom';
import { migrateUnscopedStorage } from '../utils/orgStorage';
const STORAGE_KEYS = {
    'General Labour': 'managd-swms-general-labour',
    'Telehandler': 'managd-swms-telehandler',
    'Traffic Management': 'managd-swms-traffic-management',
    'Elevated Work Platform (Over 11m)': 'managd-swms-elevated-work-platform-over-11m',
    'Demolition': 'managd-swms-demolition',
}

const formatDisplayDate = value => formatDate(new Date(value), 'EEE, d MMM yyyy')

const formatDisplayDateTime = value => `${formatDisplayDate(value)}\n${formatTime(value)}`
const SWMS_LIBRARY_KEY = 'managd-swms-library'
// Library and draft data is per organisation: an unscoped key leaked one org's
// saved forms (and signatures) into every other org used in the same browser.
const orgStorageKey = (key, orgId) => `${key}-org-${orgId || 'none'}`
const UNSCOPED_STORAGE_KEYS = [
    SWMS_LIBRARY_KEY,
    ...Object.values(STORAGE_KEYS),
    'managd-daily-vehicle-checklist',
    'managd-daily-tgs-checklist',
    'managd-jsa-draft',
    'managd-incident-report-draft',
]
const swmsDraftStorageKey = (category, orgId) => orgStorageKey(
    STORAGE_KEYS[category] || `managd-swms-${String(category || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
    orgId
)

const documentationTypes = [
    'Safe Work Method Statement',
    'Job Safety Analysis',
    'Incident Report',
    'Daily Vehicle Checklist',
    'Daily TGS Checklist',
]

const swmsTypes = [
    'General Labour',
    'Telehandler',
    'Traffic Management',
    'Elevated Work Platform (Over 11m)',
    'Demolition',
]

const vehicleChecklistItems = [
    'Tyres and wheel condition',
    'Lights, indicators and reflectors',
    'Brakes and handbrake',
    'Steering',
    'Windscreen, mirrors and wipers',
    'Horn and reversing alarm',
    'Seat belts',
    'Engine oil, coolant and fluid levels',
    'Leaks under vehicle',
    'Registration and insurance current',
    'First aid kit',
    'Fire extinguisher',
    'Traffic control equipment secured',
    'Vehicle body and tray condition',
]

const emptyVehicleChecklist = {
    documentNumber: '',
    inspectionDate: '',
    inspectionTime: '',
    driverName: '',
    vehicleRegistration: '',
    vehicleMakeModel: '',
    odometer: '',
    siteProject: '',
    results: {},
    defects: '',
    correctiveAction: '',
    safeToOperate: '',
    driverSignature: '',
    supervisorName: '',
}

const tgsChecklistItems = [
    'Approved and current TGS is available on site',
    'Site setup matches the approved TGS',
    'Signs are correct, visible, clean and facing approaching traffic',
    'Sign locations, sequence and spacing are suitable',
    'Conflicting permanent signs are covered where required',
    'Temporary speed zone signs and advance warning signs are correct',
    'Cones, bollards, barriers and tapers are correctly installed',
    'Required sight distance is available to approaching road users',
    'Traffic queues are monitored and remain within advance warning',
    'Pedestrian and cyclist routes are safe, continuous and accessible',
    'Driveways, side roads and property access are safely managed',
    'Traffic controllers are positioned safely and communications work',
    'Workers, plant and the work area are separated from live traffic',
    'Night lighting and device visibility are adequate, if applicable',
    'Site changes, defects and corrective actions are recorded',
]

const emptyTgsChecklist = {
    documentNumber: '',
    inspectionDate: '',
    inspectionTime: '',
    inspectorName: '',
    qualification: '',
    projectSite: '',
    roadLocation: '',
    tgsNumber: '',
    approvedBy: '',
    postedSpeed: '',
    temporarySpeed: '',
    weather: '',
    inspectionStage: '',
    results: {},
    defects: '',
    correctiveAction: '',
    photosRecorded: '',
    safeToContinue: '',
    inspectorSignature: '',
}

const highRiskOptions = [
    'Risk of a person falling more than 2 metres',
    'Work on or near pressurised gas mains or piping',
    'Work on a telecommunication tower',
    'Work on or near chemical, fuel, or refrigerant lines',
    'Demolition of a load-bearing element',
    'Work on or near energised electrical installations or services',
    'Demolition affecting the physical integrity of a structure',
    'Work in a contaminated or flammable atmosphere',
    'Work that may disturb asbestos',
    'Tilt-up or precast concrete work',
    'Structural alteration requiring temporary support',
    'Work on or adjacent to a road, railway, shipping lane, or traffic corridor',
    'Work in or near a confined space',
    'Work near powered mobile plant',
    'Work in a shaft, tunnel, or trench deeper than 1.5 metres',
    'Work in artificial extremes of temperature',
    'Work in or near water with a drowning risk',
    'Contaminated environment',
    'Mobile cranes and lifting operations',
    'Use of explosives',
]

const machineryOptions = [
    'Forklift', 'Cranes', 'Hoists', 'Excavator', 'Backhoe / Loader', 'Boom Lift',
    'EWP', 'Genie Lift', 'Trencher', 'Drilling Rig', 'Trucks', 'Formwork',
    'Bobcat', 'Flammable Gas', 'Fuel', 'Dozer', 'High Voltage', 'Mulcher',
    'Tilt-up Panels', 'Roller', 'Scissor Lift', 'Tractor', 'None',
]

const ppeOptions = [
    'Foot Protection', 'Hand Protection', 'Head Protection', 'Hearing Protection',
    'Eye Protection', 'Respiratory Protection', 'Face Protection',
    'High-Visibility Clothing', 'Protective Clothing', 'Fall Protection',
    'Sun Protection', 'Hair / Jewellery Secured',
]

const defaultHazards = [
    ['Accessing site and starting work', 'Traffic hazards', 'High', 'Signage, flagging, or delineation of work zone. Workers to be aware of traffic hazards.', 'Moderate', 'All workers'],
    ['Accessing site and starting work', 'Contact with services', 'High', 'Maintain exclusion zones around powerlines. Use spotters if necessary. Complete DBYD and protect underground services.', 'Moderate', 'All workers'],
    ['Accessing site and starting work', 'Slips, trips and falls', 'High', 'Maintain good housekeeping, wear non-slip shoes, and pay attention when walking.', 'Moderate', 'All workers'],
    ['Conducting general construction work', 'Falls or injuries during construction work', 'High', 'Use approved fall prevention, restraint, or arrest systems. Ensure workers are trained and equipment is inspected.', 'Moderate', 'All workers'],
    ['Conducting general construction work', 'Plant and machinery hazards', 'High', 'Maintain exclusion zones. Only competent and authorised personnel may operate plant and machinery.', 'Moderate', 'All workers'],
    ['Conducting general construction work', 'Hand and power tool injuries', 'Moderate', 'Inspect tools before use, maintain guarding, use tested electrical leads, and wear suitable PPE.', 'Low', 'All workers'],
    ['Conducting general construction work', 'Manual handling injuries', 'Moderate', 'Use good posture, mechanical aids, and team lifting where required.', 'Low', 'All workers'],
    ['Completing task / pack up', 'Slips, trips, and falls', 'High', 'Store tools and equipment appropriately and place rubbish in bins.', 'Moderate', 'All workers'],
].map(([jobStep, hazards, initialRisk, controls, residualRisk, responsible]) => ({
    jobStep, hazards, initialRisk, controls, residualRisk, responsible,
}))

const telehandlerHazards = [
    ['General precautions', 'Incorrect or unauthorised use; overloading', 'High', 'Use the machine only for purposes approved by the manufacturer. Do not carry passengers or exceed the safe load charts. Always observe safe load limits.', 'Low', 'Operator'],
    ['Pre-start checks', 'Wheels and tyres; mirrors and windows; seat and seat belt; attachments', 'High', 'Inspect tyres and the machine for damage. Adjust mirrors, clean windows, inspect the seat belt, and ensure the correct approved attachment is securely fitted.', 'Low', 'Operator'],
    ['After-start checks', 'Controls; lights and signals; steering and brakes; boom and attachments', 'High', 'Check all controls, the load limiter, lights, horn, reversing signal, beacon, steering, brakes, boom movement and attachment operation.', 'Low', 'Operator'],
    ['Site safety', 'Collision; machine damage; personal injury; entanglement', 'High', 'Inspect the operating area for obstructions, unsafe ground, structures, traffic and pedestrians. Wear required PPE and keep loose clothing, jewellery and long hair secured.', 'Low', 'Operator / Site Supervisor'],
    ['Attachments', 'Incompatible or failed attachments; movement; failure under load', 'High', 'Use manufacturer-approved attachments only. Inspect load-bearing components and fork tines. Follow fitting procedures, secure attachments, and use approved lifting gear.', 'Low', 'Operator'],
    ['Operation', 'Incorrect fork position; overloading; instability; overturning; collision; unauthorised movement', 'High', 'Space and lock forks to suit the load. Check load weight and charts, retract the boom before travelling, keep loads low, observe slope limits, maintain exclusion zones and secure the machine before leaving the cab.', 'Low', 'Operator'],
    ['Cleaning and maintenance', 'Moving parts; hot components; chemicals and lubricants', 'High', 'Follow the operator manual, engage the park brake, inspect for damage, use approved parts and lubricants, allow hot parts to cool and wear suitable eye and hand protection.', 'Low', 'Operator'],
    ['Use of stabilisers', 'Instability or overturning', 'High', 'Use stabilisers only to improve stability. Level the machine before raising a load, confirm firm ground and the stabiliser indicator, and never deploy stabilisers with a load already raised.', 'Low', 'Operator'],
].map(([jobStep, hazards, initialRisk, controls, residualRisk, responsible]) => ({
    jobStep, hazards, initialRisk, controls, residualRisk, responsible,
}))

const trafficManagementHazards = [
    ['Load and prepare vehicle; drive to site', 'Traffic; oil on road surface; adverse weather; pedestrians and obstacles; vehicle malfunction; unsecured load', 'Low', 'Drive to the conditions at or below the speed limit. Observe corner speed advisories, watch for vulnerable road users, complete vehicle checks and secure the load before moving.', 'Low', 'Driver / Team Leader'],
    ['Set out signs and devices', 'Passing traffic; trucks and machinery; hand injuries; limited space; trip hazards; unsecured or damaged signage', 'Moderate', 'Maintain observation of traffic and use a spotter. Give way to works vehicles, wear gloves, avoid restricted areas where possible, watch for uneven surfaces and secure all signage and equipment.', 'Low', 'Driver / Team Leader / Traffic Controllers'],
    ['Control and manage site', 'Restricted sight distance; vehicle speed; slippery surfaces; errant vehicles; end-of-queue collisions; driveways; deliveries; reversing machinery; overhead obstacles', 'High', 'Maintain appropriate sight distance or use a spotter. Signal drivers to slow, cover conflicting speed signs, adjust sign spacing for conditions, monitor queues, maintain access, communicate with workers and check overhead clearances.', 'Low', 'Driver / Team Leader / Traffic Controllers'],
    ['Pack up site and return to depot', 'Passing traffic; trucks and machinery; hand injuries; limited space; trip hazards; unsecured or damaged signage', 'High', 'Maintain observation of traffic and use a spotter. Give way to works vehicles, wear gloves, avoid restricted areas where possible, watch for uneven surfaces and secure all signage and equipment before travel.', 'Low', 'Driver / Team Leader / Traffic Controllers'],
].map(([jobStep, hazards, initialRisk, controls, residualRisk, responsible]) => ({
    jobStep, hazards, initialRisk, controls, residualRisk, responsible,
}))

const elevatedWorkPlatformHazards = [
    ['Planning and consultation', 'Unplanned high-risk construction work; unsuitable EWP; unlicensed or untrained operator', 'Acute', 'Confirm the task and EWP are suitable for the required height, reach, load and ground conditions. A boom-type EWP with a boom length of 11 metres or more must only be operated by a person holding the applicable High Risk Work Licence. Consult workers and review the SWMS before work starts.', 'Moderate', 'Site Supervisor / EWP Operator'],
    ['Delivery, loading and unloading', 'Vehicle movement; collision; crush injury; unstable ramps; unsecured plant', 'High', 'Use a designated level loading area, traffic management and an exclusion zone. Follow the manufacturer loading procedure, use a spotter where required and secure the EWP using rated restraints for transport.', 'Low', 'Driver / EWP Operator'],
    ['Pre-start inspection', 'Mechanical, hydraulic or electrical failure; damaged controls or safety devices', 'High', 'Complete and record the manufacturer pre-start inspection. Check tyres, structure, guardrails, gates, harness anchor points, fluids, alarms, interlocks, emergency lowering and ground/platform controls. Isolate and tag out defective equipment.', 'Low', 'EWP Operator'],
    ['Set up work area', 'Soft or uneven ground; underground services; slopes; outriggers sinking; collision with people or plant', 'Acute', 'Assess ground bearing capacity, gradients, voids, trenches and underground services. Level the EWP within manufacturer limits, fully deploy outriggers and suitable spreader pads where required, and establish a barricaded exclusion zone.', 'Moderate', 'Site Supervisor / EWP Operator'],
    ['Work near overhead electric lines', 'Electric shock, arc flash or electrocution', 'Acute', 'Identify electrical hazards. De-energise and isolate wherever reasonably practicable. Maintain exclusion zones required by the relevant state or territory electrical safety regulator and use a trained safety observer where required. Treat lines as live unless formally confirmed otherwise.', 'Moderate', 'Site Supervisor / EWP Operator'],
    ['Access and operate platform', 'Fall from platform; ejection; gate left open; overloading; dropped objects', 'Acute', 'Use the designated access, close the gate and keep both feet on the platform floor. Do not climb on rails. Wear a correctly fitted harness and short restraint lanyard attached to the approved anchor point for boom-type EWPs. Observe platform load limits and secure tools and materials.', 'Moderate', 'EWP Operator'],
    ['Position and elevate platform', 'Entrapment or crushing against overhead structures; collision; overturning; blind movement', 'Acute', 'Inspect the travel path and overhead clearance, maintain effective communication and use a spotter when visibility or clearance is restricted. Operate smoothly at low speed, remain within rated limits and do not override safety devices. Use secondary guarding where suitable.', 'Moderate', 'EWP Operator / Spotter'],
    ['Work in wind or adverse weather', 'Instability, overturning, lightning, poor visibility or loss of control', 'High', 'Monitor weather and wind speed at platform height. Do not operate above the manufacturer wind rating or during lightning, severe weather or unsafe visibility. Lower the platform and cease work when conditions deteriorate.', 'Low', 'Site Supervisor / EWP Operator'],
    ['Emergency response and rescue', 'Operator incapacitation; equipment failure; suspended worker; delayed rescue', 'Acute', 'Develop and communicate a site-specific rescue plan before elevating. Ensure a trained person remains available to operate ground and emergency lowering controls. Keep access to controls clear and confirm emergency communications and first aid arrangements.', 'Moderate', 'Site Supervisor / Ground Person'],
    ['Shutdown and pack up', 'Unauthorised use; collision; stored energy; slips and trips', 'High', 'Lower and park the EWP on firm level ground in the designated area, isolate power, remove the key, secure against movement and complete the post-use inspection. Report, tag and isolate defects.', 'Low', 'EWP Operator'],
].map(([jobStep, hazards, initialRisk, controls, residualRisk, responsible]) => ({
    jobStep, hazards, initialRisk, controls, residualRisk, responsible,
}))

const demolitionHazards = [
    ['Plan and prepare demolition work', 'Incorrect demolition sequence; uncontrolled structural collapse; unlicensed work; inadequate consultation', 'Acute', 'Obtain available structural drawings and engineering advice, inspect the structure and document a safe demolition sequence. Confirm demolition licensing and regulator notification requirements with the relevant state or territory regulator. Consult workers and coordinate with the principal contractor and other duty holders before work starts.', 'Moderate', 'Demolition Supervisor / Structural Engineer'],
    ['Identify hazardous materials', 'Exposure to asbestos, lead, silica, hazardous chemicals, mould or contaminated materials', 'Acute', 'Review the asbestos register and hazardous materials survey before disturbing the structure. Arrange sampling where materials are unknown. Have asbestos and other hazardous materials removed by appropriately licensed or competent persons, establish exclusion areas and follow applicable air monitoring and clearance requirements.', 'Moderate', 'Demolition Supervisor / Licensed Removalist'],
    ['Locate and isolate services', 'Electric shock; fire; explosion; flooding; release of gas, fuel or refrigerant', 'Acute', 'Locate overhead, underground and concealed services using current plans, service authorities, inspection and detection methods. Have electricity, gas, water, communications and other services disconnected, isolated and verified by competent persons. Lock out, tag and protect services that must remain live.', 'Moderate', 'Demolition Supervisor / Licensed Trades'],
    ['Establish site and exclusion zones', 'Unauthorised access; falling debris striking workers or the public; interaction with traffic and mobile plant', 'High', 'Secure the site with fencing, barricades and warning signs. Establish and enforce exclusion zones based on the structure, demolition method and potential debris range. Provide traffic and pedestrian controls, controlled access and a spotter where plant movement creates risk.', 'Low', 'Site Supervisor / Traffic Controller'],
    ['Install temporary supports and access', 'Premature structural failure; collapse of floors, walls or adjoining structures; falls from height', 'Acute', 'Install engineered propping, bracing and temporary supports before weakening load-bearing elements. Verify floor loading and protect adjoining structures. Provide compliant scaffolds, edge protection or other fall prevention systems and inspect them before use and after changes.', 'Moderate', 'Structural Engineer / Demolition Supervisor'],
    ['Soft strip and manual demolition', 'Falls; cuts; punctures; manual handling injuries; dust; falling materials; unsafe hand or power tools', 'High', 'Work from stable access, remove materials in the planned sequence and prevent work above or below other persons. Use mechanical aids and team lifting, inspected tools with guards and dust suppression or extraction. Remove protruding nails and maintain good housekeeping.', 'Moderate', 'Demolition Workers / Supervisor'],
    ['Mechanical demolition', 'Plant rollover; collision; structural collapse onto plant; flying debris; excessive floor loading', 'Acute', 'Use plant and attachments selected for the structure and operated by competent, licensed persons where required. Confirm ground and suspended-floor capacity, maintain stand-off distances and exclusion zones, use a spotter and demolish progressively in the engineered sequence. Do not overload floors with plant or debris.', 'Moderate', 'Plant Operator / Demolition Supervisor'],
    ['Control falling and removed materials', 'Uncontrolled collapse; falling objects; overloaded floors; blocked access; fire', 'Acute', 'Do not allow materials to free-fall outside controlled chutes or exclusion zones. Lower large components using engineered lifting methods and licensed dogging or rigging where required. Remove debris progressively, keep emergency routes clear and do not exceed verified floor capacities.', 'Moderate', 'Demolition Supervisor / Workers'],
    ['Manage dust, noise and vibration', 'Respirable crystalline silica exposure; hearing damage; reduced visibility; damage to adjoining structures', 'High', 'Use wet methods, on-tool extraction and suitable respiratory protective equipment with fit testing where required. Isolate noisy work, wear hearing protection and monitor dust, noise and vibration where indicated by the risk assessment. Stop work if movement or damage is detected.', 'Low', 'Demolition Supervisor / Workers'],
    ['Weather, fire and emergency response', 'High winds; unstable structure; fire; collapse; trapped or injured persons', 'Acute', 'Monitor weather and cease work when wind or other conditions affect stability or control. Maintain firefighting equipment and control ignition sources. Brief workers on alarms, evacuation, first aid and a site-specific collapse and rescue plan; ensure emergency access remains available.', 'Moderate', 'Site Supervisor / All Workers'],
    ['Final inspection and site handover', 'Residual unstable elements; sharp objects; open penetrations; remaining services or hazardous materials', 'High', 'Have a competent person inspect the completed work and confirm remaining structures are stable. Guard openings, remove loose materials and waste, verify service status and hazardous-material clearance, and keep exclusion zones in place until the area is formally released as safe.', 'Low', 'Demolition Supervisor'],
].map(([jobStep, hazards, initialRisk, controls, residualRisk, responsible]) => ({
    jobStep, hazards, initialRisk, controls, residualRisk, responsible,
}))

const localDateValue = (date = new Date()) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}
const emptyPerson = () => ({ name: '', position: '', signature: '', date: new Date().toISOString() })
const emptyReview = (reviewNumber = '') => ({ reviewNumber, name: '', date: localDateValue(), comments: '' })

const initialForm = {
    swmsNumber: '',
    approverName: '',
    approverTitle: '',
    approvalDate: '',
    client: '',
    projectName: '',
    projectAddress: '',
    manager: '',
    contactPhone: '',
    suppliedDate: '',
    highRisk: [],
    machinery: [],
    otherMachinery: '',
    ppe: [],
    consultation: [emptyPerson()],
    hazards: defaultHazards,
    signatories: [emptyPerson()],
    reviews: [emptyReview('1')],
}

const initialTelehandlerForm = {
    ...initialForm,
    projectName: '',
    hazards: telehandlerHazards,
}

const initialTrafficManagementForm = {
    ...initialForm,
    projectName: '',
    hazards: trafficManagementHazards,
}

const initialElevatedWorkPlatformForm = {
    ...initialForm,
    projectName: '',
    highRisk: ['Risk of a person falling more than 2 metres', 'Work near powered mobile plant'],
    machinery: ['EWP'],
    ppe: ['Foot Protection', 'Head Protection', 'High-Visibility Clothing', 'Fall Protection'],
    hazards: elevatedWorkPlatformHazards,
}

const initialDemolitionForm = {
    ...initialForm,
    projectName: '',
    highRisk: [
        'Demolition of a load-bearing element',
        'Demolition affecting the physical integrity of a structure',
        'Work that may disturb asbestos',
        'Structural alteration requiring temporary support',
    ],
    machinery: [],
    ppe: ['Foot Protection', 'Hand Protection', 'Head Protection', 'Hearing Protection', 'Eye Protection', 'High-Visibility Clothing', 'Respiratory Protection'],
    hazards: demolitionHazards,
}

const createSwmsForms = (projectDetails = {}, selectedCategory = null, savedForm = null) => {
    const applyProjectDetails = (baseForm) => {
        const today = new Date()
        return {
            ...baseForm,
            approverName: projectDetails.approverName || baseForm.approverName,
            approverTitle: projectDetails.approverTitle || baseForm.approverTitle || 'Supervisor',
            approvalDate: projectDetails.approvalDate || baseForm.approvalDate || today,
            client: projectDetails.clientName || baseForm.client,
            projectName: projectDetails.projectName || baseForm.projectName,
            projectAddress: projectDetails.address || baseForm.projectAddress,
            manager: projectDetails.primaryContactName || baseForm.manager,
            contactPhone: projectDetails.primaryContactNumber || baseForm.contactPhone,
            suppliedDate: projectDetails.suppliedDate || baseForm.suppliedDate || today,
            consultation: (baseForm.consultation || []).map((person) => ({
                ...person,
                date: person.date || today.toISOString(),
            })),
            signatories: (baseForm.signatories || []).map((person) => ({
                ...person,
                date: person.date || today.toISOString(),
            })),
            reviews: (baseForm.reviews || []).map((review) => ({
                ...review,
                date: review.date || localDateValue(today),
            })),
        }
    }

    const forms = {
        'General Labour': applyProjectDetails(initialForm),
        'Telehandler': applyProjectDetails(initialTelehandlerForm),
        'Traffic Management': applyProjectDetails(initialTrafficManagementForm),
        'Elevated Work Platform (Over 11m)': applyProjectDetails(initialElevatedWorkPlatformForm),
        'Demolition': applyProjectDetails(initialDemolitionForm),
    }

    if (selectedCategory && savedForm && typeof savedForm === 'object' && forms[selectedCategory]) {
        const defaults = forms[selectedCategory]
        const merged = { ...defaults, ...savedForm }
        // Forms saved by other clients (older mobile builds) or older versions can
        // lack sections or store them as text; keep the template's list there.
        Object.keys(defaults).forEach(field => {
            if (Array.isArray(defaults[field]) && !Array.isArray(merged[field])) merged[field] = defaults[field]
        })
        forms[selectedCategory] = merged
    }

    return forms
}

const createChecklistDocumentNumber = type => {
    const year = new Date().getFullYear()
    const counterKey = `managd-${type.toLowerCase()}-checklist-counter-${year}`
    const nextNumber = (Number(localStorage.getItem(counterKey)) || 0) + 1
    localStorage.setItem(counterKey, String(nextNumber))
    return `${type} - ${year}${String(nextNumber).padStart(3, '0')}`
}

const ensureChecklistDocumentNumber = (checklist, type) => (
    checklist.documentNumber
        ? checklist
        : { ...checklist, documentNumber: createChecklistDocumentNumber(type) }
)

const DailyVehicleChecklist = ({ setSuccess, orgId }) => {
    const storageKey = orgStorageKey('managd-daily-vehicle-checklist', orgId)
    const [checklist, setChecklist] = useState(emptyVehicleChecklist)

    useEffect(() => {
        try {
            const saved = localStorage.getItem(storageKey)
            if (saved) {
                const savedChecklist = JSON.parse(saved)
                setChecklist({
                    ...emptyVehicleChecklist,
                    ...savedChecklist,
                })
            }
        } catch {
            // Keep a clean checklist if a previous draft is invalid.
        }
    }, [storageKey])

    const setValue = (field, value) => setChecklist(previous => ({ ...previous, [field]: value }))
    const setResult = (item, value) => setChecklist(previous => ({
        ...previous,
        results: { ...previous.results, [item]: value },
    }))
    const saveChecklist = () => {
        const checklistToSave = ensureChecklistDocumentNumber(checklist, 'DVC')
        setChecklist(checklistToSave)
        localStorage.setItem(storageKey, JSON.stringify(checklistToSave))
        setSuccess({ title: 'Checklist saved', message: 'Daily Vehicle Checklist saved successfully.' })
    }

    return <>
        <div className='documentationFormHeading'>
            <div>
                <h2>Daily Vehicle Checklist</h2>
                <p>Complete before the vehicle is used for the day.</p>
            </div>
            <span className='documentationDraftBadge'>Draft</span>
        </div>
        <div className='documentationFormStack'>
            <Section title='Inspection Details' variant='card'>
                <div className='documentationFieldGrid'>
                    <FieldLabel className='documentationField'><span>Document Number</span><UiInput value={checklist.documentNumber} placeholder='Generated on save' disabled /></FieldLabel>
                    <div className='documentationField'><MyCalendar showDateOnly noMaxDate startDate={checklist.inspectionDate} setStartDate={value => setValue('inspectionDate', value)} label='Inspection Date' /></div>
                    <FieldLabel className='documentationField'><span>Inspection Time</span><DateInput type='time' value={checklist.inspectionTime} onChange={event => setValue('inspectionTime', event.target.value)} /></FieldLabel>
                    <FieldLabel className='documentationField'><span>Driver Name</span><UiInput value={formatName(checklist.driverName)} onChange={event => setValue('driverName', event.target.value)} /></FieldLabel>
                    <FieldLabel className='documentationField'><span>Vehicle Registration</span><UiInput value={checklist.vehicleRegistration} onChange={event => setValue('vehicleRegistration', event.target.value.toUpperCase())} /></FieldLabel>
                    <FieldLabel className='documentationField'><span>Vehicle Make / Model</span><UiInput value={checklist.vehicleMakeModel} onChange={event => setValue('vehicleMakeModel', event.target.value)} /></FieldLabel>
                    <FieldLabel className='documentationField'><span>Odometer Reading</span><UiInput type='number' min='0' value={checklist.odometer} onChange={event => setValue('odometer', event.target.value)} /></FieldLabel>
                    <FieldLabel className='documentationField documentationFieldWide'><span>Site / Project</span><UiInput value={checklist.siteProject} onChange={event => setValue('siteProject', event.target.value)} /></FieldLabel>
                </div>
            </Section>

            <Section title='Vehicle Pre-Start Inspection' variant='card'>
                <div className='vehicleChecklistTable'>
                    <div className='vehicleChecklistHeader'><span>Inspection Item</span><span>Result</span></div>
                    {vehicleChecklistItems.map(item => (
                        <div className='vehicleChecklistRow' key={item}>
                            <span>{item}</span>
                            <div className='vehicleChecklistOptions'>
                                {['Pass', 'Fail', 'N/A'].map(result => (
                                    <Button variant="secondary" size="md"
                                        type='button'
                                        className={checklist.results[item] === result ? `vehicleChecklistOption vehicleChecklistOption${result.replace('/', '')}Active` : 'vehicleChecklistOption'}
                                        onClick={() => setResult(item, result)}
                                        key={result}
                                    >
                                        {result}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </Section>

            <Section title='Defects and Corrective Action' variant='card'>
                <div className='documentationFieldGrid'>
                    <FieldLabel className='documentationField documentationFieldWide'><span>Defects / Damage Identified</span><Textarea value={checklist.defects} onChange={event => setValue('defects', event.target.value)} placeholder='Enter details, or write Nil' /></FieldLabel>
                    <FieldLabel className='documentationField documentationFieldWide'><span>Corrective Action Taken / Required</span><Textarea value={checklist.correctiveAction} onChange={event => setValue('correctiveAction', event.target.value)} /></FieldLabel>
                    <FieldLabel className='documentationField'><span>Vehicle Safe to Operate</span><UiSelect value={checklist.safeToOperate} onChange={event => setValue('safeToOperate', event.target.value)}><option value=''>Select</option><option>Yes</option><option>No</option></UiSelect></FieldLabel>
                    <FieldLabel className='documentationField'><span>Driver Signature / Name</span><UiInput value={checklist.driverSignature} onChange={event => setValue('driverSignature', event.target.value)} /></FieldLabel>
                    <FieldLabel className='documentationField'><span>Supervisor Name</span><UiInput value={formatName(checklist.supervisorName)} onChange={event => setValue('supervisorName', event.target.value)} /></FieldLabel>
                </div>
            </Section>
        </div>
        <div className='documentationStickyActions'>
            <Button data-action-tone="save" variant="soft" size="md"  type='button' className='actionWithIcon' onClick={saveChecklist}>Save Checklist</Button>
        </div>
    </>
}

const DailyTgsChecklist = ({ setSuccess, orgId }) => {
    const storageKey = orgStorageKey('managd-daily-tgs-checklist', orgId)
    const [checklist, setChecklist] = useState(emptyTgsChecklist)

    useEffect(() => {
        try {
            const saved = localStorage.getItem(storageKey)
            if (saved) {
                const savedChecklist = JSON.parse(saved)
                setChecklist({
                    ...emptyTgsChecklist,
                    ...savedChecklist,
                })
            }
        } catch {
            // Keep a clean checklist if a previous draft is invalid.
        }
    }, [storageKey])

    const setValue = (field, value) => setChecklist(previous => ({ ...previous, [field]: value }))
    const setResult = (item, value) => setChecklist(previous => ({
        ...previous,
        results: { ...previous.results, [item]: value },
    }))
    const saveChecklist = () => {
        const checklistToSave = ensureChecklistDocumentNumber(checklist, 'TGS')
        setChecklist(checklistToSave)
        localStorage.setItem(storageKey, JSON.stringify(checklistToSave))
        setSuccess({ title: 'Checklist saved', message: 'Daily TGS Checklist saved successfully.' })
    }

    return <>
        <div className='documentationFormHeading'>
            <div>
                <h2>Traffic Guidance Scheme Daily Checklist</h2>
                <p>Daily inspection record based on TCAWS and AGTTM temporary traffic management guidance.</p>
            </div>
            <span className='documentationDraftBadge'>Draft</span>
        </div>
        <div className='documentationFormStack'>
            <Section title='Site and Inspection Details' variant='card'>
                <div className='documentationFieldGrid'>
                    <FieldLabel className='documentationField'><span>Document Number</span><UiInput value={checklist.documentNumber} placeholder='Generated on save' disabled /></FieldLabel>
                    <div className='documentationField'><MyCalendar showDateOnly noMaxDate startDate={checklist.inspectionDate} setStartDate={value => setValue('inspectionDate', value)} label='Inspection Date' /></div>
                    <FieldLabel className='documentationField'><span>Inspection Time</span><DateInput type='time' value={checklist.inspectionTime} onChange={event => setValue('inspectionTime', event.target.value)} /></FieldLabel>
                    <FieldLabel className='documentationField'><span>Inspector Name</span><UiInput value={formatName(checklist.inspectorName)} onChange={event => setValue('inspectorName', event.target.value)} /></FieldLabel>
                    <FieldLabel className='documentationField'><span>Qualification / Card Number</span><UiInput value={checklist.qualification} onChange={event => setValue('qualification', event.target.value)} /></FieldLabel>
                    <FieldLabel className='documentationField'><span>Project / Site</span><UiInput value={checklist.projectSite} onChange={event => setValue('projectSite', event.target.value)} /></FieldLabel>
                    <FieldLabel className='documentationField'><span>Road / Location</span><UiInput value={checklist.roadLocation} onChange={event => setValue('roadLocation', event.target.value)} /></FieldLabel>
                    <FieldLabel className='documentationField'><span>TGS Number / Revision</span><UiInput value={checklist.tgsNumber} onChange={event => setValue('tgsNumber', event.target.value)} /></FieldLabel>
                    <FieldLabel className='documentationField'><span>TGS Approved By</span><UiInput value={checklist.approvedBy} onChange={event => setValue('approvedBy', event.target.value)} /></FieldLabel>
                    <FieldLabel className='documentationField'><span>Posted Speed</span><UiInput value={checklist.postedSpeed} onChange={event => setValue('postedSpeed', event.target.value)} placeholder='e.g. 60 km/h' /></FieldLabel>
                    <FieldLabel className='documentationField'><span>Temporary Speed</span><UiInput value={checklist.temporarySpeed} onChange={event => setValue('temporarySpeed', event.target.value)} placeholder='e.g. 40 km/h' /></FieldLabel>
                    <FieldLabel className='documentationField'><span>Weather / Road Conditions</span><UiInput value={checklist.weather} onChange={event => setValue('weather', event.target.value)} /></FieldLabel>
                    <FieldLabel className='documentationField'><span>Inspection Stage</span><UiSelect value={checklist.inspectionStage} onChange={event => setValue('inspectionStage', event.target.value)}><option value=''>Select</option><option>Before work starts</option><option>During works</option><option>Pre-closedown</option><option>After change to TGS</option></UiSelect></FieldLabel>
                </div>
            </Section>

            <Section title='Daily TGS Inspection' variant='card'>
                <div className='vehicleChecklistTable'>
                    <div className='vehicleChecklistHeader'><span>Inspection Item</span><span>Result</span></div>
                    {tgsChecklistItems.map(item => (
                        <div className='vehicleChecklistRow' key={item}>
                            <span>{item}</span>
                            <div className='vehicleChecklistOptions'>
                                {['Compliant', 'Action', 'N/A'].map(result => (
                                    <Button variant="secondary" size="md"
                                        type='button'
                                        className={checklist.results[item] === result ? `vehicleChecklistOption vehicleChecklistOption${result.replace('/', '')}Active` : 'vehicleChecklistOption'}
                                        onClick={() => setResult(item, result)}
                                        key={result}
                                    >
                                        {result}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </Section>

            <Section title='Actions and Sign-Off' variant='card'>
                <div className='documentationFieldGrid'>
                    <FieldLabel className='documentationField documentationFieldWide'><span>Defects / Variations Identified</span><Textarea value={checklist.defects} onChange={event => setValue('defects', event.target.value)} placeholder='Enter details, or write Nil' /></FieldLabel>
                    <FieldLabel className='documentationField documentationFieldWide'><span>Corrective Action Taken</span><Textarea value={checklist.correctiveAction} onChange={event => setValue('correctiveAction', event.target.value)} /></FieldLabel>
                    <FieldLabel className='documentationField'><span>Photographic Record Completed</span><UiSelect value={checklist.photosRecorded} onChange={event => setValue('photosRecorded', event.target.value)}><option value=''>Select</option><option>Yes</option><option>No</option><option>N/A</option></UiSelect></FieldLabel>
                    <FieldLabel className='documentationField'><span>TGS Safe to Continue</span><UiSelect value={checklist.safeToContinue} onChange={event => setValue('safeToContinue', event.target.value)}><option value=''>Select</option><option>Yes</option><option>No</option></UiSelect></FieldLabel>
                    <FieldLabel className='documentationField'><span>Inspector Signature / Name</span><UiInput value={checklist.inspectorSignature} onChange={event => setValue('inspectorSignature', event.target.value)} /></FieldLabel>
                </div>
            </Section>
        </div>
        <div className='documentationStickyActions'>
            <Button data-action-tone="save" variant="soft" size="md"  type='button' className='actionWithIcon' onClick={saveChecklist}>Save Checklist</Button>
        </div>
    </>
}

const createIncidentNumber = () => {
    const year = new Date().getFullYear()
    const counterKey = `managd-incident-report-counter-${year}`
    const nextNumber = (Number(localStorage.getItem(counterKey)) || 0) + 1
    localStorage.setItem(counterKey, String(nextNumber))
    return `IR - ${year}${nextNumber}`
}

const emptyIncidentPerson = () => ({ fullName: '', role: '', company: '', contactNumber: '', injury: '' })
const emptyIncidentWitness = () => ({ fullName: '', contactNumber: '', statement: '' })
const emptyCorrectiveAction = () => ({ action: '', responsiblePerson: '', dueDate: '', status: 'Open' })

const createJsaNumber = () => {
    const year = new Date().getFullYear()
    const counterKey = `managd-jsa-counter-${year}`
    const nextNumber = (Number(localStorage.getItem(counterKey)) || 0) + 1
    localStorage.setItem(counterKey, String(nextNumber))
    return `JSA - ${year}${String(nextNumber).padStart(3, '0')}`
}

const emptyJsaStep = () => ({
    jobStep: '',
    hazard: '',
    initialRisk: '',
    controlMeasures: '',
    hierarchy: '',
    residualRisk: '',
    responsiblePerson: '',
})

const emptyJsaConsultation = () => ({ fullName: '', position: '', date: '', signature: '' })

const JobSafetyAnalysis = ({ setSuccess, orgId }) => {
    const draftKey = orgStorageKey('managd-jsa-draft', orgId)
    const [jsa, setJsa] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(draftKey) || 'null')
            if (saved) return saved
        } catch {
            // Start with a clean JSA if the saved draft cannot be read.
        }
        return {
            jsaNumber: createJsaNumber(),
            businessName: 'Multicrew Pty Ltd',
            projectName: '',
            client: '',
            siteAddress: '',
            task: '',
            workArea: '',
            preparedBy: '',
            position: '',
            datePrepared: new Date().toISOString(),
            reviewDate: '',
            supervisor: '',
            workersConsulted: '',
            plantEquipment: '',
            hazardousChemicals: '',
            permitsLicences: '',
            emergencyArrangements: '',
            ppe: [],
            steps: [emptyJsaStep()],
            consultation: [emptyJsaConsultation()],
            authorisedBy: '',
            authoriserPosition: '',
            approvalDate: '',
            monitoringReview: '',
        }
    })

    const capitaliseWords = value => value.replace(/\b[a-z]/g, letter => letter.toUpperCase())
    const setField = (field, value) => setJsa(previous => ({ ...previous, [field]: value }))
    const updateRow = (field, index, key, value) => setJsa(previous => ({
        ...previous,
        [field]: previous[field].map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row),
    }))
    const addRow = (field, row) => setJsa(previous => ({ ...previous, [field]: [...previous[field], row] }))
    const removeRow = (field, index) => setJsa(previous => ({
        ...previous,
        [field]: previous[field].filter((_, rowIndex) => rowIndex !== index),
    }))
    const togglePpe = item => setJsa(previous => ({
        ...previous,
        ppe: previous.ppe.includes(item) ? previous.ppe.filter(value => value !== item) : [...previous.ppe, item],
    }))
    const saveJsa = () => {
        localStorage.setItem(draftKey, JSON.stringify(jsa))
        setSuccess({ title: 'Job Safety Analysis saved', message: `${jsa.jsaNumber} was saved successfully.` })
    }
    const textField = (label, field, transform) => (
        <FieldLabel className='documentationField'>
            <span>{label}</span>
            <UiInput value={jsa[field]} onChange={event => setField(field, transform ? transform(event.target.value) : event.target.value)} />
        </FieldLabel>
    )
    const riskOptions = ['', 'Low', 'Moderate', 'High', 'Extreme']
    const hierarchyOptions = ['', 'Elimination', 'Substitution', 'Isolation', 'Engineering Controls', 'Administrative Controls', 'Personal Protective Equipment (PPE)']
    const jsaPpeOptions = [
        'Safety Footwear', 'High-Visibility Clothing', 'Hard Hat', 'Safety Glasses',
        'Gloves', 'Hearing Protection', 'Respiratory Protection', 'Fall Protection',
        'Sun Protection', 'Other',
    ]

    return <>
        <div className='documentationFormHeading'>
            <div>
                <h2>Job Safety Analysis</h2>
                <p>Identify each job step, assess its WHS risks, and record effective control measures.</p>
            </div>
            <span className='documentationDraftBadge'>Draft</span>
        </div>
        <div className='documentationFormStack'>
            <Section title='JSA Details' variant='card'>
                <div className='documentationFieldGrid'>
                    <FieldLabel className='documentationField'><span>JSA Number</span><UiInput value={jsa.jsaNumber} disabled /></FieldLabel>
                    {textField('Business / PCBU', 'businessName', capitaliseWords)}
                    {textField('Project Name', 'projectName', capitaliseWords)}
                    {textField('Client / Principal Contractor', 'client', capitaliseWords)}
                    {textField('Site Address', 'siteAddress', capitaliseWords)}
                    {textField('Task / Activity', 'task', capitaliseWords)}
                    {textField('Work Area', 'workArea', capitaliseWords)}
                    {textField('Prepared By - Full Name', 'preparedBy', capitaliseWords)}
                    {textField('Position', 'position', capitaliseWords)}
                    <div className='documentationField'><MyCalendar showDateOnly noMaxDate startDate={jsa.datePrepared} setStartDate={value => setField('datePrepared', value)} label='Date Prepared' /></div>
                    <div className='documentationField'><MyCalendar showDateOnly noMaxDate startDate={jsa.reviewDate} setStartDate={value => setField('reviewDate', value)} label='Review Date' /></div>
                    {textField('Site Supervisor', 'supervisor', capitaliseWords)}
                </div>
            </Section>

            <Section title='Planning and Work Requirements' variant='card'>
                <div className='documentationFieldGrid'>
                    <FieldLabel className='documentationField documentationFieldWide'><span>Plant, Tools and Equipment</span><Textarea value={jsa.plantEquipment} onChange={event => setField('plantEquipment', event.target.value)} /></FieldLabel>
                    <FieldLabel className='documentationField documentationFieldWide'><span>Hazardous Chemicals / SDS Requirements</span><Textarea value={jsa.hazardousChemicals} onChange={event => setField('hazardousChemicals', event.target.value)} placeholder='List substances and confirm current Safety Data Sheets are available, or enter N/A' /></FieldLabel>
                    <FieldLabel className='documentationField documentationFieldWide'><span>Permits, Licences, Competencies and Isolations</span><Textarea value={jsa.permitsLicences} onChange={event => setField('permitsLicences', event.target.value)} /></FieldLabel>
                    <FieldLabel className='documentationField documentationFieldWide'><span>Emergency and First Aid Arrangements</span><Textarea value={jsa.emergencyArrangements} onChange={event => setField('emergencyArrangements', event.target.value)} /></FieldLabel>
                </div>
            </Section>

            <Section title='Personal Protective Equipment (PPE)' variant='card'>
                <div className='documentationCheckGrid documentationCheckGridCompact'>
                    {jsaPpeOptions.map(item => (
                        <FieldLabel choice key={item} className={jsa.ppe.includes(item) ? 'documentationCheck documentationCheckActive' : 'documentationCheck'}>
                            <UiCheckbox type='checkbox' checked={jsa.ppe.includes(item)} onChange={() => togglePpe(item)} />
                            <span>{item}</span>
                        </FieldLabel>
                    ))}
                </div>
            </Section>

            <Section title='Job Steps, Hazards and Controls' variant='card'>
                <p className='documentationSectionHint'>Apply the hierarchy of controls and reduce each risk so far as is reasonably practicable.</p>
                <div className='documentationReviewList'>
                    {jsa.steps.map((step, index) => (
                        <div className='documentationReviewCard' key={`jsa-step-${index}`}>
                            <h4>Job Step {index + 1}</h4>
                            <div className='documentationFieldGrid'>
                                <FieldLabel className='documentationField documentationFieldWide'><span>Job Step / Sequence of Work *</span><Textarea value={step.jobStep} onChange={event => updateRow('steps', index, 'jobStep', event.target.value)} /></FieldLabel>
                                <FieldLabel className='documentationField documentationFieldWide'><span>Hazards and Potential Harm *</span><Textarea value={step.hazard} onChange={event => updateRow('steps', index, 'hazard', event.target.value)} /></FieldLabel>
                                <FieldLabel className='documentationField'><span>Initial Risk *</span><UiSelect value={step.initialRisk} onChange={event => updateRow('steps', index, 'initialRisk', event.target.value)}>{riskOptions.map(option => <option key={option} value={option}>{option || 'Select risk'}</option>)}</UiSelect></FieldLabel>
                                <FieldLabel className='documentationField'><span>Highest Applicable Control Level *</span><UiSelect value={step.hierarchy} onChange={event => updateRow('steps', index, 'hierarchy', event.target.value)}>{hierarchyOptions.map(option => <option key={option} value={option}>{option || 'Select control level'}</option>)}</UiSelect></FieldLabel>
                                <FieldLabel className='documentationField documentationFieldWide'><span>Control Measures *</span><Textarea value={step.controlMeasures} onChange={event => updateRow('steps', index, 'controlMeasures', event.target.value)} /></FieldLabel>
                                <FieldLabel className='documentationField'><span>Residual Risk *</span><UiSelect value={step.residualRisk} onChange={event => updateRow('steps', index, 'residualRisk', event.target.value)}>{riskOptions.map(option => <option key={option} value={option}>{option || 'Select risk'}</option>)}</UiSelect></FieldLabel>
                                <FieldLabel className='documentationField'><span>Responsible Person *</span><UiInput value={step.responsiblePerson} onChange={event => updateRow('steps', index, 'responsiblePerson', capitaliseWords(event.target.value))} /></FieldLabel>
                            </div>
                            {jsa.steps.length > 1 && <Button data-action-tone="delete" variant="danger" size="md"  type='button' className='textButton textButtonDanger' onClick={() => removeRow('steps', index)}>Delete</Button>}
                        </div>
                    ))}
                </div>
                <Button variant="create" size="md" type='button' className='documentationAddRowButton' onClick={() => addRow('steps', emptyJsaStep())}><PlusIcon />Add Job Step</Button>
            </Section>

            <Section title='Worker Consultation and Acknowledgement' variant='card'>
                <p className='documentationSectionHint'>Workers must be consulted, understand the controls, and stop work if conditions change or controls are ineffective.</p>
                <div className='documentationReviewList'>
                    {jsa.consultation.map((person, index) => (
                        <div className='documentationReviewCard' key={`jsa-person-${index}`}>
                            <div className='documentationFieldGrid'>
                                <FieldLabel className='documentationField'><span>Full Name *</span><UiInput value={person.fullName} onChange={event => updateRow('consultation', index, 'fullName', capitaliseWords(event.target.value))} /></FieldLabel>
                                <FieldLabel className='documentationField'><span>Position / Trade *</span><UiInput value={person.position} onChange={event => updateRow('consultation', index, 'position', capitaliseWords(event.target.value))} /></FieldLabel>
                                <div className='documentationField'><MyCalendar showDateOnly noMaxDate startDate={person.date} setStartDate={value => updateRow('consultation', index, 'date', value)} label='Date *' /></div>
                                <FieldLabel className='documentationField'><span>Signature *</span><UiInput value={person.signature} onChange={event => updateRow('consultation', index, 'signature', event.target.value)} placeholder='Signature' /></FieldLabel>
                            </div>
                            {jsa.consultation.length > 1 && <Button data-action-tone="delete" variant="danger" size="md"  type='button' className='textButton textButtonDanger' onClick={() => removeRow('consultation', index)}>Delete</Button>}
                        </div>
                    ))}
                </div>
                <Button variant="create" size="md" type='button' className='documentationAddRowButton' onClick={() => addRow('consultation', emptyJsaConsultation())}><PlusIcon />Add Person</Button>
            </Section>

            <Section title='Authorisation, Monitoring and Review' variant='card'>
                <div className='documentationFieldGrid'>
                    {textField('Authorised By - Full Name', 'authorisedBy', capitaliseWords)}
                    {textField('Position', 'authoriserPosition', capitaliseWords)}
                    <div className='documentationField'><MyCalendar showDateOnly noMaxDate startDate={jsa.approvalDate} setStartDate={value => setField('approvalDate', value)} label='Approval Date' /></div>
                    <FieldLabel className='documentationField documentationFieldWide'><span>Monitoring and Review Arrangements</span><Textarea value={jsa.monitoringReview} onChange={event => setField('monitoringReview', event.target.value)} placeholder='Review when the task, workplace or conditions change, a new hazard is identified, after an incident, or when controls are ineffective.' /></FieldLabel>
                </div>
            </Section>

            <div className='documentationComplianceNote'>
                A JSA does not replace a Safe Work Method Statement where high-risk construction work requires one under applicable WHS laws.
            </div>
        </div>
        <div className='documentationStickyActions'>
            <Button data-action-tone="save" variant="soft" size="md"  type='button' className='actionWithIcon' onClick={saveJsa}>Save Job Safety Analysis</Button>
        </div>
    </>
}

const IncidentReport = ({ setSuccess, orgId }) => {
    const draftKey = orgStorageKey('managd-incident-report-draft', orgId)
    const [report, setReport] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(draftKey) || 'null')
            if (saved) return saved
        } catch {
            // Start with a clean report if the saved draft cannot be read.
        }
        return {
            incidentNumber: createIncidentNumber(),
            incidentDate: '',
            incidentTime: '',
            reportedDate: new Date().toISOString(),
            projectName: '',
            siteLocation: '',
            exactLocation: '',
            reportedBy: '',
            reporterPosition: '',
            reporterContact: '',
            classifications: [],
            personsInvolved: [emptyIncidentPerson()],
            activityBeforeIncident: '',
            incidentDescription: '',
            immediateActions: '',
            injuryDetails: '',
            treatmentProvided: '',
            treatmentProvider: '',
            hospital: '',
            witnesses: [emptyIncidentWitness()],
            supervisorNotified: '',
            regulatorNotified: '',
            emergencyServices: '',
            rootCause: '',
            contributingFactors: '',
            investigationFindings: '',
            correctiveActions: [emptyCorrectiveAction()],
            reviewedBy: '',
            reviewerPosition: '',
            reviewDate: '',
        }
    })

    const setField = (field, value) => setReport(previous => ({ ...previous, [field]: value }))
    const updateRow = (field, index, key, value) => setReport(previous => ({
        ...previous,
        [field]: previous[field].map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row),
    }))
    const addRow = (field, row) => setReport(previous => ({ ...previous, [field]: [...previous[field], row] }))
    const removeRow = (field, index) => setReport(previous => ({
        ...previous,
        [field]: previous[field].filter((_, rowIndex) => rowIndex !== index),
    }))
    const toggleClassification = value => setReport(previous => ({
        ...previous,
        classifications: previous.classifications.includes(value)
            ? previous.classifications.filter(item => item !== value)
            : [...previous.classifications, value],
    }))
    const capitaliseWords = value => value.replace(/\b[a-z]/g, letter => letter.toUpperCase())
    const textField = (label, field, type = 'text', transform) => (
        <FieldLabel className='documentationField'>
            <span>{label}</span>
            <UiInput type={type} value={report[field]} onChange={event => setField(field, transform ? transform(event.target.value) : event.target.value)} />
        </FieldLabel>
    )
    const saveIncidentReport = () => {
        localStorage.setItem(draftKey, JSON.stringify(report))
        setSuccess({ title: 'Incident report saved', message: `${report.incidentNumber} was saved successfully.` })
    }

    const classifications = [
        'Injury / Illness', 'Near Miss', 'Property Damage', 'Environmental',
        'Vehicle Incident', 'Security', 'Public Incident', 'Other',
    ]

    return <>
        <div className='documentationFormHeading'>
            <div>
                <h2>Incident Report</h2>
                <p>Record the incident, response, investigation, and corrective actions.</p>
            </div>
            <span className='documentationDraftBadge'>Draft</span>
        </div>
        <div className='documentationFormStack'>
            <Section title='Incident Details' variant='card'>
                <div className='documentationFieldGrid'>
                    <FieldLabel className='documentationField'><span>Incident Number</span><UiInput value={report.incidentNumber} disabled /></FieldLabel>
                    <div className='documentationField'><MyCalendar showDateOnly noMaxDate startDate={report.incidentDate} setStartDate={value => setField('incidentDate', value)} label='Incident Date' /></div>
                    {textField('Incident Time', 'incidentTime', 'time')}
                    <div className='documentationField'><MyCalendar showDateOnly noMaxDate startDate={report.reportedDate} setStartDate={value => setField('reportedDate', value)} label='Date Reported' /></div>
                    {textField('Project Name', 'projectName', 'text', capitaliseWords)}
                    {textField('Site Location', 'siteLocation', 'text', capitaliseWords)}
                    {textField('Exact Incident Location', 'exactLocation', 'text', capitaliseWords)}
                </div>
            </Section>

            <Section title='Reported By' variant='card'>
                <div className='documentationFieldGrid'>
                    {textField('Full Name', 'reportedBy', 'text', capitaliseWords)}
                    {textField('Position', 'reporterPosition', 'text', capitaliseWords)}
                    {textField('Contact Number', 'reporterContact', 'tel')}
                </div>
            </Section>

            <Section title='Incident Classification' variant='card'>
                <div className='documentationCheckGrid documentationCheckGridCompact'>
                    {classifications.map(option => (
                        <FieldLabel choice key={option} className={report.classifications.includes(option) ? 'documentationCheck documentationCheckActive' : 'documentationCheck'}>
                            <UiCheckbox type='checkbox' checked={report.classifications.includes(option)} onChange={() => toggleClassification(option)} />
                            <span>{option}</span>
                        </FieldLabel>
                    ))}
                </div>
            </Section>

            <Section title='Persons Involved' variant='card'>
                <div className='documentationReviewList'>
                    {report.personsInvolved.map((person, index) => (
                        <div className='documentationReviewCard' key={`incident-person-${index}`}>
                            <div className='documentationFieldGrid'>
                                {[
                                    ['Full Name', 'fullName'], ['Role / Position', 'role'], ['Company', 'company'],
                                    ['Contact Number', 'contactNumber'], ['Injury / Impact', 'injury'],
                                ].map(([label, key]) => (
                                    <FieldLabel className='documentationField' key={key}>
                                        <span>{label}</span>
                                        <UiInput value={person[key]} onChange={event => updateRow('personsInvolved', index, key, event.target.value)} />
                                    </FieldLabel>
                                ))}
                            </div>
                            <Button data-action-tone="delete" variant="danger" size="md"  type='button' className='textButton textButtonDanger' onClick={() => removeRow('personsInvolved', index)}>Delete</Button>
                        </div>
                    ))}
                </div>
                <Button variant="create" size="md" type='button' className='documentationAddRowButton' onClick={() => addRow('personsInvolved', emptyIncidentPerson())}><PlusIcon />Add Person</Button>
            </Section>

            <Section title='Incident Description and Immediate Response' variant='card'>
                <div className='documentationFieldGrid'>
                    <FieldLabel className='documentationField documentationFieldWide'><span>Activity Before the Incident</span><Textarea value={report.activityBeforeIncident} onChange={event => setField('activityBeforeIncident', event.target.value)} /></FieldLabel>
                    <FieldLabel className='documentationField documentationFieldWide'><span>What Happened</span><Textarea value={report.incidentDescription} onChange={event => setField('incidentDescription', event.target.value)} /></FieldLabel>
                    <FieldLabel className='documentationField documentationFieldWide'><span>Immediate Actions Taken</span><Textarea value={report.immediateActions} onChange={event => setField('immediateActions', event.target.value)} /></FieldLabel>
                </div>
            </Section>

            <Section title='Injury and Treatment Details' variant='card'>
                <div className='documentationFieldGrid'>
                    <FieldLabel className='documentationField documentationFieldWide'><span>Injury Details</span><Textarea value={report.injuryDetails} onChange={event => setField('injuryDetails', event.target.value)} /></FieldLabel>
                    <FieldLabel className='documentationField documentationFieldWide'><span>Treatment Provided</span><Textarea value={report.treatmentProvided} onChange={event => setField('treatmentProvided', event.target.value)} /></FieldLabel>
                    {textField('Treatment Provided By', 'treatmentProvider', 'text', capitaliseWords)}
                    {textField('Hospital / Medical Centre', 'hospital', 'text', capitaliseWords)}
                </div>
            </Section>

            <Section title='Witnesses' variant='card'>
                <div className='documentationReviewList'>
                    {report.witnesses.map((witness, index) => (
                        <div className='documentationReviewCard' key={`incident-witness-${index}`}>
                            <div className='documentationFieldGrid'>
                                <FieldLabel className='documentationField'><span>Full Name</span><UiInput value={witness.fullName} onChange={event => updateRow('witnesses', index, 'fullName', event.target.value)} /></FieldLabel>
                                <FieldLabel className='documentationField'><span>Contact Number</span><UiInput value={witness.contactNumber} onChange={event => updateRow('witnesses', index, 'contactNumber', event.target.value)} /></FieldLabel>
                                <FieldLabel className='documentationField documentationFieldWide'><span>Witness Statement</span><Textarea value={witness.statement} onChange={event => updateRow('witnesses', index, 'statement', event.target.value)} /></FieldLabel>
                            </div>
                            <Button data-action-tone="delete" variant="danger" size="md"  type='button' className='textButton textButtonDanger' onClick={() => removeRow('witnesses', index)}>Delete</Button>
                        </div>
                    ))}
                </div>
                <Button variant="create" size="md" type='button' className='documentationAddRowButton' onClick={() => addRow('witnesses', emptyIncidentWitness())}><PlusIcon />Add Witness</Button>
            </Section>

            <Section title='Notifications and Investigation' variant='card'>
                <div className='documentationFieldGrid'>
                    {textField('Supervisor Notified', 'supervisorNotified')}
                    {textField('Regulator Notified', 'regulatorNotified')}
                    {textField('Emergency Services / Police', 'emergencyServices')}
                    <FieldLabel className='documentationField documentationFieldWide'><span>Root Cause</span><Textarea value={report.rootCause} onChange={event => setField('rootCause', event.target.value)} /></FieldLabel>
                    <FieldLabel className='documentationField documentationFieldWide'><span>Contributing Factors</span><Textarea value={report.contributingFactors} onChange={event => setField('contributingFactors', event.target.value)} /></FieldLabel>
                    <FieldLabel className='documentationField documentationFieldWide'><span>Investigation Findings</span><Textarea value={report.investigationFindings} onChange={event => setField('investigationFindings', event.target.value)} /></FieldLabel>
                </div>
            </Section>

            <Section title='Corrective Actions' variant='card'>
                <div className='documentationReviewList'>
                    {report.correctiveActions.map((action, index) => (
                        <div className='documentationReviewCard' key={`corrective-action-${index}`}>
                            <div className='documentationFieldGrid'>
                                <FieldLabel className='documentationField documentationFieldWide'><span>Corrective Action</span><Textarea value={action.action} onChange={event => updateRow('correctiveActions', index, 'action', event.target.value)} /></FieldLabel>
                                <FieldLabel className='documentationField'><span>Responsible Person</span><UiInput value={action.responsiblePerson} onChange={event => updateRow('correctiveActions', index, 'responsiblePerson', event.target.value)} /></FieldLabel>
                                <FieldLabel className='documentationField'><span>Due Date</span><DateInput type='date' value={action.dueDate} onChange={event => updateRow('correctiveActions', index, 'dueDate', event.target.value)} /></FieldLabel>
                                <FieldLabel className='documentationField'><span>Status</span><UiSelect value={action.status} onChange={event => updateRow('correctiveActions', index, 'status', event.target.value)}><option>Open</option><option>In Progress</option><option>Completed</option></UiSelect></FieldLabel>
                            </div>
                            <Button data-action-tone="delete" variant="danger" size="md"  type='button' className='textButton textButtonDanger' onClick={() => removeRow('correctiveActions', index)}>Delete</Button>
                        </div>
                    ))}
                </div>
                <Button variant="create" size="md" type='button' className='documentationAddRowButton' onClick={() => addRow('correctiveActions', emptyCorrectiveAction())}><PlusIcon />Add Corrective Action</Button>
            </Section>

            <Section title='Review and Sign-Off' variant='card'>
                <div className='documentationFieldGrid'>
                    {textField('Reviewed By - Full Name', 'reviewedBy', 'text', capitaliseWords)}
                    {textField('Position', 'reviewerPosition', 'text', capitaliseWords)}
                    <div className='documentationField'><MyCalendar showDateOnly noMaxDate startDate={report.reviewDate} setStartDate={value => setField('reviewDate', value)} label='Review Date' /></div>
                </div>
            </Section>
        </div>
        <div className='documentationStickyActions'>
            <Button data-action-tone="save" variant="soft" size="md"  type='button' className='actionWithIcon' onClick={saveIncidentReport}>Save Incident Report</Button>
        </div>
    </>
}

const Documentation = ({
    embedded = false,
    initialType = null,
    initialSwmsType = null,
    initialProjectDetails = {},
    initialFormData = null,
    initialReadOnly = false,
    hideFormHeading = false,
    initialAttachToAllocations = false,
    onFormSaved,
    onCancel,
}) => {
    const navigate = useNavigate()
    const userInfo = useSelector((state) => state.user.user)
    const activeOrg = useSelector((state) => state.user.activeOrg)
    // Before anything below reads saved drafts or the library.
    migrateUnscopedStorage(activeOrg?.id, UNSCOPED_STORAGE_KEYS, orgStorageKey)
    const currentUserName = formatName([userInfo?.firstName, userInfo?.lastName].filter(Boolean).join(' ').trim())
    const [forms, setForms] = useState(() => createSwmsForms({
        ...initialProjectDetails,
        approverName: initialProjectDetails.approverName || currentUserName,
        approverTitle: initialProjectDetails.approverTitle || 'Supervisor',
    }, initialSwmsType, initialFormData))
    const [success, setSuccess] = useState(null)
    const [selectedType, setSelectedType] = useState(initialType)
    const [selectedSwmsType, setSelectedSwmsType] = useState(initialSwmsType)
    const [signaturePersonIndex, setSignaturePersonIndex] = useState(null)
    const [signatureSignatoryIndex, setSignatureSignatoryIndex] = useState(null)
    const [signatureHasInk, setSignatureHasInk] = useState(false)
    const [riskItemConfirmation, setRiskItemConfirmation] = useState(null)
    const [swmsLibrary, setSwmsLibrary] = useState([])
    const [removedApproverRows, setRemovedApproverRows] = useState({})
    const [showSaveFileModal, setShowSaveFileModal] = useState(false)
    const [saveFileName, setSaveFileName] = useState('')
    const [viewingLibraryItem, setViewingLibraryItem] = useState(null)
    const [deletingLibraryItem, setDeletingLibraryItem] = useState(null)
    const [editingLibraryItem, setEditingLibraryItem] = useState(null)
    const [clients, setClients] = useState([])
    const [sites, setSites] = useState([])
    const [selectedClientId, setSelectedClientId] = useState(initialProjectDetails.clientId || '')
    const [selectedSiteId, setSelectedSiteId] = useState(initialProjectDetails.siteId || '')
    const [savingForm, setSavingForm] = useState(false)
    const [isReadOnly, setIsReadOnly] = useState(initialReadOnly)
    const [attachToAllocations, setAttachToAllocations] = useState(initialAttachToAllocations)
    const signatureCanvasRef = useRef(null)
    const pendingLibraryEditRef = useRef(false)
    const returnToSiteTimerRef = useRef(null)
    const form = forms[selectedSwmsType] || initialForm
    const approvalDetailsComplete = Boolean(
        form.approverName?.trim()
        && form.approverTitle?.trim()
        && form.approvalDate
    )
    const projectDetailsComplete = Boolean(
        form.client?.trim()
        && form.projectName?.trim()
        && form.projectAddress?.trim()
        && form.manager?.trim()
        && form.contactPhone?.trim()
        && form.suppliedDate
    )
    const consultationComplete = form.consultation.some(person => (
        person.name?.trim()
        && person.position?.trim()
        && person.signature
        && person.date
    ))
    const highRiskComplete = form.highRisk.length > 0
    const machineryComplete = form.machinery.length > 0
    const ppeComplete = form.ppe.length > 0
    const presetHazardCount = selectedSwmsType === 'Telehandler'
        ? telehandlerHazards.length
        : selectedSwmsType === 'Traffic Management'
            ? trafficManagementHazards.length
            : selectedSwmsType === 'Elevated Work Platform (Over 11m)'
                ? elevatedWorkPlatformHazards.length
                : selectedSwmsType === 'Demolition'
                    ? demolitionHazards.length
                    : defaultHazards.length
    const isRiskItemComplete = hazard => (
        hazard.jobStep?.trim()
        && hazard.hazards?.trim()
        && hazard.initialRisk
        && hazard.controls?.trim()
        && hazard.residualRisk
        && hazard.responsible?.trim()
    )
    const additionalRiskItemsComplete = form.hazards
        .slice(presetHazardCount)
        .every(isRiskItemComplete)
    const signatoriesComplete = form.signatories.length > 0 && form.signatories.every(person => (
        person.name?.trim()
        && person.position?.trim()
        && person.signature
        && person.date
    ))
    const firstConsultationPerson = form.consultation[0] || null
    const createFreshSwmsForm = category => createSwmsForms({
        ...initialProjectDetails,
        approverName: initialProjectDetails.approverName || currentUserName,
        approverTitle: initialProjectDetails.approverTitle || 'Supervisor',
    })[category] || initialForm
    // Older library entries predate some sections (signatories, consultation,
    // hazards); fill anything missing from the category template.
    const withSwmsDefaults = (category, savedForm) => {
        const defaults = createFreshSwmsForm(category)
        const merged = { ...defaults, ...savedForm }
        ;['highRisk', 'machinery', 'ppe', 'consultation', 'hazards', 'signatories', 'reviews'].forEach(field => {
            if (!Array.isArray(merged[field])) merged[field] = defaults[field]
        })
        return merged
    }
    // Leaving a pending library edit for another form/category abandons it, so a
    // later save creates a new entry rather than overwriting the edited one.
    const abandonLibraryEdit = nextCategory => {
        if (!editingLibraryItem || editingLibraryItem.category === nextCategory) return
        const editedCategory = editingLibraryItem.category
        setForms(previous => ({ ...previous, [editedCategory]: createFreshSwmsForm(editedCategory) }))
        pendingLibraryEditRef.current = false
        setEditingLibraryItem(null)
    }
    const setForm = updater => setForms(previous => {
        const currentForm = previous[selectedSwmsType] || initialForm
        let nextForm = typeof updater === 'function' ? updater(currentForm) : updater

        if (pendingLibraryEditRef.current) {
            const reviews = nextForm.reviews?.length ? nextForm.reviews : [emptyReview('1')]
            const currentNumber = Number.parseInt(reviews[0].reviewNumber, 10) || 1
            nextForm = {
                ...nextForm,
                reviews: reviews.map((review, index) => index === 0
                    ? { ...review, reviewNumber: String(currentNumber + 1) }
                    : review),
            }
            pendingLibraryEditRef.current = false
        }

        return {
            ...previous,
            [selectedSwmsType]: nextForm,
        }
    })

    useEffect(() => {
        if (!selectedSwmsType) return
        if (removedApproverRows[selectedSwmsType]) return

        setForms(previous => {
            const currentForm = previous[selectedSwmsType] || initialForm
            const firstPerson = currentForm.consultation[0] || emptyPerson()
            if (
                firstPerson.name === currentForm.approverName
                && firstPerson.position === currentForm.approverTitle
                && firstPerson.autoPopulated
                && currentForm.consultation.length
            ) {
                return previous
            }

            const consultation = currentForm.consultation.length
                ? currentForm.consultation.map((person, index) => index === 0
                    ? { ...person, name: currentForm.approverName, position: currentForm.approverTitle, autoPopulated: true }
                    : person)
                : [{ ...firstPerson, name: currentForm.approverName, position: currentForm.approverTitle, autoPopulated: true }]

            return {
                ...previous,
                [selectedSwmsType]: { ...currentForm, consultation },
            }
        })
    }, [selectedSwmsType, form.approverName, form.approverTitle, removedApproverRows])

    useEffect(() => {
        if (!selectedSwmsType) return
        const sourcePerson = firstConsultationPerson
        if (!sourcePerson) return

        setForms(previous => {
            const currentForm = previous[selectedSwmsType] || initialForm
            const firstSignatory = currentForm.signatories[0] || emptyPerson()
            if (
                firstSignatory.name === sourcePerson.name
                && firstSignatory.position === sourcePerson.position
                && firstSignatory.signature === sourcePerson.signature
                && firstSignatory.date === sourcePerson.date
                && firstSignatory.autoPopulated
            ) {
                return previous
            }

            const populatedSignatory = {
                ...firstSignatory,
                name: sourcePerson.name,
                position: sourcePerson.position,
                signature: sourcePerson.signature,
                date: sourcePerson.date,
                autoPopulated: true,
            }
            const signatories = currentForm.signatories.length
                ? currentForm.signatories.map((person, index) => index === 0 ? populatedSignatory : person)
                : [populatedSignatory]

            return {
                ...previous,
                [selectedSwmsType]: { ...currentForm, signatories },
            }
        })
    }, [selectedSwmsType, firstConsultationPerson])

    const ensureSwmsNumber = (swmsType) => {
        setForms(previous => {
            const currentForm = previous[swmsType] || initialForm
            if (currentForm.swmsNumber) return previous

            const year = new Date().getFullYear()
            const assignedNumberKey = `managd-swms-number-${swmsType.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
            let swmsNumber = localStorage.getItem(assignedNumberKey)

            if (!swmsNumber) {
                const counterKey = `managd-swms-number-counter-${year}`
                const nextSequence = (Number(localStorage.getItem(counterKey)) || 0) + 1
                localStorage.setItem(counterKey, String(nextSequence))
                swmsNumber = `SWMS - ${year}${nextSequence}`
                localStorage.setItem(assignedNumberKey, swmsNumber)
            }

            return {
                ...previous,
                [swmsType]: { ...currentForm, swmsNumber },
            }
        })
    }

    useEffect(() => {
        if (initialSwmsType) ensureSwmsNumber(initialSwmsType)
        // The embedded form is initialised once for the selected site/category.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!currentUserName) return
        setForms((previous) => Object.fromEntries(Object.entries(previous).map(([category, currentForm]) => [
            category,
            {
                ...currentForm,
                approverName: currentForm.approverName || currentUserName,
                approverTitle: currentForm.approverTitle || 'Supervisor',
                approvalDate: currentForm.approvalDate || new Date(),
                suppliedDate: currentForm.suppliedDate || new Date(),
            },
        ])))
    }, [currentUserName])

    useEffect(() => {
        if (!activeOrg?.id) return
        const loadClients = async () => {
            try {
                const response = await postWithAuth('fetchClients', { orgId: activeOrg.id })
                if (response?.err) {
                    toast.error(response.err)
                    return
                }
                setClients(response?.data || [])
            } catch (error) {
                toast.error(error.message)
            }
        }
        loadClients()
    }, [activeOrg?.id])

    useEffect(() => {
        if (!activeOrg?.id || !selectedClientId) {
            setSites([])
            return
        }
        // Ignore replies for a client/org that is no longer selected, so a slow
        // earlier request cannot overwrite the current client's sites.
        let cancelled = false
        const loadSites = async () => {
            try {
                const response = await postWithAuth('fetchSites', {
                    orgId: activeOrg.id,
                    clientId: selectedClientId,
                })
                if (cancelled) return
                if (response?.err) {
                    toast.error(response.err)
                    return
                }
                setSites(response?.data || [])
            } catch (error) {
                if (!cancelled) toast.error(error.message)
            }
        }
        loadSites()
        return () => { cancelled = true }
    }, [activeOrg?.id, selectedClientId])

    useEffect(() => () => {
        if (returnToSiteTimerRef.current) window.clearTimeout(returnToSiteTimerRef.current)
    }, [])

    useEffect(() => {
        if (embedded) return
        setForms(previous => {
            const restored = { ...previous }
            Object.keys(STORAGE_KEYS).forEach((type) => {
                try {
                    const saved = localStorage.getItem(swmsDraftStorageKey(type, activeOrg?.id))
                    const defaults = type === 'Telehandler'
                        ? initialTelehandlerForm
                        : type === 'Traffic Management'
                            ? initialTrafficManagementForm
                            : type === 'Elevated Work Platform (Over 11m)'
                                ? initialElevatedWorkPlatformForm
                                : type === 'Demolition'
                                    ? initialDemolitionForm
                                    : initialForm
                    if (saved) {
                        const savedForm = { ...defaults, ...JSON.parse(saved) }
                        restored[type] = {
                            ...savedForm,
                            approverName: savedForm.approverName || currentUserName,
                            approverTitle: savedForm.approverTitle || 'Supervisor',
                            approvalDate: savedForm.approvalDate || new Date(),
                            suppliedDate: savedForm.suppliedDate || new Date(),
                        }
                    }
                } catch {
                    // Keep a clean template if an old draft is invalid.
                }
            })
            return restored
        })
    }, [embedded, currentUserName, activeOrg?.id])

    useEffect(() => {
        try {
            const savedLibrary = JSON.parse(localStorage.getItem(orgStorageKey(SWMS_LIBRARY_KEY, activeOrg?.id)) || '[]')
            setSwmsLibrary(Array.isArray(savedLibrary) ? savedLibrary : [])
        } catch {
            setSwmsLibrary([])
        }
    }, [activeOrg?.id])

    const setField = (field, value) => setForm(previous => ({ ...previous, [field]: value }))
    const clientOptions = clients.map((client) => ({ label: client.name, value: client.id }))
    const siteOptions = sites.map((site) => ({ label: site.name, value: site.id }))
    const selectClient = (option) => {
        const clientId = option?.value || ''
        const client = clients.find((item) => item.id === clientId)
        const primaryContact = client?.contacts?.find((contact) => contact.isPrimary) || {}
        const primaryContactName = formatName([
            client?.primaryFirstName || primaryContact.firstName,
            client?.primaryLastName || primaryContact.lastName,
        ].filter(Boolean).join(' ')).trim()
        const primaryContactNumber = client?.primaryContactNo || primaryContact.contactNo || client?.contactNo || ''

        setSelectedClientId(clientId)
        setSelectedSiteId('')
        setSites([])
        setForm((previous) => ({
            ...previous,
            client: client?.name || '',
            projectName: '',
            projectAddress: '',
            manager: primaryContactName,
            contactPhone: primaryContactNumber,
        }))
    }
    const selectSite = (option) => {
        const siteId = option?.value || ''
        const site = sites.find((item) => item.id === siteId)
        setSelectedSiteId(siteId)
        setForm((previous) => ({
            ...previous,
            projectName: site?.name || '',
            projectAddress: site?.address || '',
        }))
    }
    const toggleOption = (field, value) => setForm(previous => ({
        ...previous,
        [field]: previous[field].includes(value)
            ? previous[field].filter(item => item !== value)
            : [...previous[field], value],
    }))
    const toggleMachineryOption = value => setForm(previous => ({
        ...previous,
        machinery: value === 'None'
            ? (previous.machinery.includes('None') ? [] : ['None'])
            : previous.machinery.includes(value)
                ? previous.machinery.filter(item => item !== value)
                : [...previous.machinery.filter(item => item !== 'None'), value],
    }))
    const updateRow = (field, index, key, value) => setForm(previous => ({
        ...previous,
        [field]: previous[field].map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row),
    }))
    const addRow = (field, row) => setForm(previous => ({ ...previous, [field]: [...previous[field], row] }))
    const addReviewRow = () => setForm(previous => {
        const highestReviewNumber = previous.reviews.reduce(
            (highest, review) => Math.max(highest, Number.parseInt(review.reviewNumber, 10) || 0),
            0
        )
        return { ...previous, reviews: [...previous.reviews, emptyReview(String(highestReviewNumber + 1))] }
    })
    const removeRow = (field, index) => setForm(previous => ({
        ...previous,
        [field]: previous[field].filter((_, rowIndex) => rowIndex !== index),
    }))
    const removeConsultationPerson = index => {
        if (form.consultation[index]?.autoPopulated) {
            setRemovedApproverRows(previous => ({ ...previous, [selectedSwmsType]: true }))
        }
        removeRow('consultation', index)
    }
    const saveFormToSelectedSite = async (entry) => {
        const targetSite = sites.find((site) => site.id === selectedSiteId)
        if (!targetSite || !activeOrg?.id || !selectedClientId) {
            throw new Error('Select a client and project site before saving this form.')
        }

        const pdfBlob = downloadLibraryItem(entry, 'blob')
        const uploaded = await uploadFileToStorage({
            file: pdfBlob,
            path: `SiteDocs/forms/${activeOrg.id}/${selectedSiteId}/${entry.id}.pdf`,
            mimeType: 'application/pdf',
            fallbackName: `${entry.id}.pdf`,
            orgId: activeOrg.id,
        })
        const siteDocument = {
            ...entry,
            formType: entry.type,
            picture: {
                ...uploaded,
                pdf: true,
                confidential: false,
            },
        }
        // Only the site's documents change, and the server applies it inside a
        // transaction: posting the whole site from the list loaded when the client
        // was picked dropped documents added since and reverted other site fields.
        const response = await postWithAuth('saveSiteDocument', {
            orgId: activeOrg.id,
            siteId: targetSite.id,
            document: siteDocument,
        })
        const savedPictures = response?.data?.pictures

        setSites((currentSites) => currentSites.map((site) => (
            site.id === targetSite.id
                ? {
                    ...site,
                    pictures: Array.isArray(savedPictures)
                        ? savedPictures
                        : [siteDocument, ...(site.pictures || []).filter(picture => picture?.id !== entry.id)],
                }
                : site
        )))
        return siteDocument
    }

    const persistDraft = async (fileName) => {
        const trimmedFileName = fileName.trim()
        if (!trimmedFileName) return
        if (!embedded && (!selectedClientId || !selectedSiteId)) {
            toast.error('Select a client and project site before saving this form.')
            return
        }

        try {
            setSavingForm(true)
            const savedCategory = selectedSwmsType
            const savedAt = new Date().toISOString()
            let entry = {
                // Editing a saved form replaces it; it must not add a second copy. Only
                // while that same form type is open: switching to another type mid-edit
                // must not overwrite the form being edited.
                id: (editingLibraryItem?.id && editingLibraryItem.category === selectedSwmsType)
                    ? editingLibraryItem.id
                    : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                type: 'Safe Work Method Statement',
                category: selectedSwmsType,
                name: trimmedFileName,
                date: savedAt,
                savedAt,
                form,
            }

            if (!embedded) {
                entry = await saveFormToSelectedSite(entry)
            }

            // The saved form lives in the library now; the next new SWMS of this
            // category must start clean, not carry over its signatures and dates.
            localStorage.removeItem(swmsDraftStorageKey(savedCategory, activeOrg?.id))
            setForms(previous => ({ ...previous, [savedCategory]: createFreshSwmsForm(savedCategory) }))
            setSwmsLibrary(previous => {
                const nextLibrary = [entry, ...previous.filter(item => item?.id !== entry.id)]
                localStorage.setItem(orgStorageKey(SWMS_LIBRARY_KEY, activeOrg?.id), JSON.stringify(nextLibrary))
                return nextLibrary
            })
            setShowSaveFileModal(false)
            setSaveFileName('')
            pendingLibraryEditRef.current = false
            setEditingLibraryItem(null)
            if (!embedded) {
                setSuccess({
                    title: 'Form saved',
                    message: `${trimmedFileName} was saved to the selected site's documents.`,
                })
            }
            onFormSaved?.(
                entry,
                embedded ? downloadLibraryItem(entry, 'blob') : null,
                { includeInNewAllocation: attachToAllocations },
            )
            if (!embedded) {
                returnToSiteTimerRef.current = window.setTimeout(() => {
                    navigate(`/clients?clientId=${encodeURIComponent(selectedClientId)}&siteId=${encodeURIComponent(selectedSiteId)}`)
                }, 1200)
            }
        } catch (error) {
            toast.error(error.message || 'Unable to save this form.')
        } finally {
            setSavingForm(false)
        }
    }

    const saveDraft = () => {
        persistDraft(saveFileName)
    }

    const saveEmbeddedDraft = () => {
        const today = new Intl.DateTimeFormat('en-AU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }).format(new Date())
        persistDraft(`Safe Work Method Statement - ${selectedSwmsType} - ${today}`)
    }

    const saveStandaloneDraft = () => {
        const today = new Intl.DateTimeFormat('en-AU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }).format(new Date())
        persistDraft(`Safe Work Method Statement - ${selectedSwmsType} - ${today}`)
    }

    const editLibraryItem = item => {
        let savedForm = item.form
        if (!savedForm) {
            try {
                savedForm = JSON.parse(localStorage.getItem(swmsDraftStorageKey(item.category, activeOrg?.id)) || 'null')
            } catch {
                savedForm = null
            }
        }
        if (!savedForm) return
        savedForm = withSwmsDefaults(item.category, savedForm)

        setForms(previous => ({ ...previous, [item.category]: savedForm }))
        pendingLibraryEditRef.current = true
        setEditingLibraryItem({ ...item, form: savedForm })
        setSelectedType(documentationTypes[0])
        setSelectedSwmsType(item.category)
    }

    const cancelLibraryEdit = () => {
        if (editingLibraryItem?.form) {
            setForms(previous => ({
                ...previous,
                [editingLibraryItem.category]: editingLibraryItem.form,
            }))
        }
        pendingLibraryEditRef.current = false
        setEditingLibraryItem(null)
        setSelectedSwmsType(null)
    }

    const deleteLibraryItem = item => {
        setSwmsLibrary(previous => {
            const nextLibrary = previous.filter(entry => (entry.id || `${entry.category}-${entry.savedAt}`) !== (item.id || `${item.category}-${item.savedAt}`))
            localStorage.setItem(orgStorageKey(SWMS_LIBRARY_KEY, activeOrg?.id), JSON.stringify(nextLibrary))
            return nextLibrary
        })
        setDeletingLibraryItem(null)
        setSuccess({ title: 'File deleted', message: `${item.name} was removed from the Library.` })
    }

    const downloadLibraryItem = (item, output = 'save') => {
        if (!item.form) return

        const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
        const pageWidth = pdf.internal.pageSize.getWidth()
        const pageHeight = pdf.internal.pageSize.getHeight()
        const margin = 12
        const contentWidth = pageWidth - (margin * 2)
        let y = margin

        const addPageHeader = () => {
            const headerTitle = /^SWMS\s*-/i.test(item.name) ? item.name : `SWMS - ${item.name}`
            pdf.setFillColor(242, 244, 247)
            pdf.roundedRect(margin, margin, contentWidth, 16, 2, 2, 'F')
            pdf.setDrawColor(190, 198, 208)
            pdf.roundedRect(margin, margin, contentWidth, 16, 2, 2, 'S')
            pdf.setTextColor(16, 24, 40)
            pdf.setFont('helvetica', 'bold')
            pdf.setFontSize(13)
            pdf.text(headerTitle, margin + 5, margin + 6.5)
            pdf.setFont('helvetica', 'normal')
            pdf.setFontSize(8)
            pdf.text(`Safe Work Method Statement - ${item.category}`, margin + 5, margin + 12)
            pdf.setFont('helvetica', 'bold')
            pdf.text(item.form.swmsNumber || '', pageWidth - margin - 5, margin + 10, { align: 'right' })
            pdf.setTextColor(16, 24, 40)
            y = margin + 21
        }

        const ensureSpace = height => {
            if (y + height > pageHeight - margin) {
                pdf.addPage()
                addPageHeader()
            }
        }

        const addSection = (title, fields, columns = 3, compact = false) => {
            const gap = compact ? 2 : 3
            const cellWidth = (contentWidth - ((columns - 1) * gap) - 10) / columns
            const rows = Math.ceil(fields.length / columns)
            const cardHeight = compact ? 10 : 13
            const headingHeight = compact ? 9 : 12
            const sectionHeight = headingHeight + (rows * cardHeight) + ((rows - 1) * gap) + (compact ? 4 : 6)
            ensureSpace(sectionHeight + 4)

            pdf.setDrawColor(190, 198, 208)
            pdf.setFillColor(255, 255, 255)
            pdf.roundedRect(margin, y, contentWidth, sectionHeight, 2, 2, 'FD')
            pdf.setFont('helvetica', 'bold')
            pdf.setFontSize(11)
            pdf.setTextColor(16, 24, 40)
            pdf.text(title, margin + 5, y + (compact ? 6 : 7))

            fields.forEach(([label, value], index) => {
                const column = index % columns
                const row = Math.floor(index / columns)
                const x = margin + 5 + (column * (cellWidth + gap))
                const cellY = y + headingHeight + (row * (cardHeight + gap))
                pdf.setFillColor(248, 250, 252)
                pdf.setDrawColor(208, 213, 221)
                pdf.roundedRect(x, cellY, cellWidth, cardHeight, 1.5, 1.5, 'FD')
                pdf.setFont('helvetica', 'normal')
                pdf.setFontSize(7)
                pdf.setTextColor(102, 112, 133)
                pdf.text(label, x + 3, cellY + (compact ? 3.2 : 4))
                pdf.setFont('helvetica', 'bold')
                pdf.setFontSize(8)
                pdf.setTextColor(16, 24, 40)
                const valueLines = pdf.splitTextToSize(String(value || '-'), cellWidth - 6).slice(0, 2)
                pdf.text(valueLines, x + 3, cellY + (compact ? 7 : 9))
            })
            y += sectionHeight + 4
        }

        const addPeopleSection = (title, people = []) => {
            const rows = people.length || 1
            const sectionHeight = 18 + (rows * 13)
            ensureSpace(sectionHeight + 4)
            const innerX = margin + 5
            const innerWidth = contentWidth - 10
            const columnWidths = [innerWidth * .28, innerWidth * .24, innerWidth * .28, innerWidth * .20]
            const columnLabels = ['Full Name', 'Position', 'Signature', 'Date']

            pdf.setDrawColor(190, 198, 208)
            pdf.setFillColor(255, 255, 255)
            pdf.roundedRect(margin, y, contentWidth, sectionHeight, 2, 2, 'FD')
            pdf.setFont('helvetica', 'bold')
            pdf.setFontSize(11)
            pdf.setTextColor(16, 24, 40)
            pdf.text(title, innerX, y + 7)

            let columnX = innerX
            pdf.setFillColor(248, 250, 252)
            pdf.rect(innerX, y + 10, innerWidth, 7, 'F')
            columnLabels.forEach((label, index) => {
                pdf.setFontSize(7)
                pdf.setTextColor(102, 112, 133)
                pdf.text(label, columnX + 2, y + 14.5)
                columnX += columnWidths[index]
            })

            const displayPeople = people.length ? people : [emptyPerson()]
            displayPeople.forEach((person, rowIndex) => {
                const rowY = y + 17 + (rowIndex * 13)
                pdf.setDrawColor(208, 213, 221)
                pdf.line(innerX, rowY + 13, innerX + innerWidth, rowY + 13)
                pdf.setFont('helvetica', 'normal')
                pdf.setFontSize(8)
                pdf.setTextColor(16, 24, 40)
                pdf.text(pdf.splitTextToSize(person.name || '-', columnWidths[0] - 4).slice(0, 2), innerX + 2, rowY + 6)
                pdf.text(pdf.splitTextToSize(person.position || '-', columnWidths[1] - 4).slice(0, 2), innerX + columnWidths[0] + 2, rowY + 6)
                if (person.signature?.startsWith('data:image')) {
                    try {
                        pdf.addImage(person.signature, 'PNG', innerX + columnWidths[0] + columnWidths[1] + 2, rowY + 1.5, 28, 9)
                    } catch {
                        pdf.text('Signed', innerX + columnWidths[0] + columnWidths[1] + 2, rowY + 6)
                    }
                } else {
                    pdf.text(person.signature ? 'Signed' : '-', innerX + columnWidths[0] + columnWidths[1] + 2, rowY + 6)
                }
                const dateX = innerX + columnWidths[0] + columnWidths[1] + columnWidths[2] + 2
                const date = person.date ? formatDisplayDate(person.date) : '-'
                pdf.text(date, dateX, rowY + 6)
            })
            y += sectionHeight + 4
        }

        addPageHeader()
        addSection('SWMS Details and Approval', [
            ['Business Name', 'Multicrew Pty Ltd'],
            ['ABN', '93668879294'],
            ['Business Address', 'Shop 2/40 Wollongong Rd, Arncliffe NSW 2205'],
            ['Approved By - Full Name', item.form.approverName],
            ['Position', item.form.approverTitle],
            ['Approval Date', item.form.approvalDate ? formatDisplayDate(item.form.approvalDate) : '-'],
        ], 3, true)
        addSection('Project and Principal Contractor Details', [
            ['Client', item.form.client],
            ['Project Name', item.form.projectName],
            ['Project Address', item.form.projectAddress],
            ['Site Manager', item.form.manager],
            ['Contact Number', item.form.contactPhone],
            ['Date Supplied', item.form.suppliedDate ? formatDisplayDate(item.form.suppliedDate) : '-'],
        ], 3, true)
        addPeopleSection('Personnel Consulted and Communicated', item.form.consultation)
        addPeopleSection('Signatories of the Safe Work Method Statement', item.form.signatories)
        addSection('Work Selections', [
            ['High-Risk Construction Work', item.form.highRisk?.join(', ')],
            ['Machinery / Equipment', item.form.machinery?.join(', ')],
            ['Personal Protective Equipment', item.form.ppe?.join(', ')],
        ])

        pdf.addPage()
        addPageHeader()
        pdf.setFont('helvetica', 'bold')
        pdf.setFontSize(12)
        pdf.text('Job Steps, Hazards and Controls', margin, y + 5)
        y += 9
        item.form.hazards?.forEach((hazard, index) => {
            const controls = pdf.splitTextToSize(String(hazard.controls || '-'), contentWidth - 12)
            const cardHeight = Math.max(25, 21 + (controls.length * 3.5))
            ensureSpace(cardHeight + 4)
            pdf.setDrawColor(190, 198, 208)
            pdf.setFillColor(252, 253, 253)
            pdf.roundedRect(margin, y, contentWidth, cardHeight, 2, 2, 'FD')
            pdf.setFillColor(208, 213, 221)
            pdf.rect(margin, y, 2, cardHeight, 'F')
            pdf.setTextColor(16, 24, 40)
            pdf.setFont('helvetica', 'bold')
            pdf.setFontSize(9)
            pdf.text(`Risk Item ${index + 1}: ${hazard.jobStep || '-'}`, margin + 6, y + 6)
            pdf.setFont('helvetica', 'normal')
            pdf.setFontSize(8)
            pdf.text(`Potential Hazards: ${hazard.hazards || '-'}`, margin + 6, y + 12)
            pdf.text(`Initial Risk: ${hazard.initialRisk || '-'}    Residual Risk: ${hazard.residualRisk || '-'}    Responsible: ${hazard.responsible || '-'}`, margin + 6, y + 17)
            pdf.text(controls, margin + 6, y + 22)
            y += cardHeight + 4
        })

        const totalPages = pdf.getNumberOfPages()
        for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
            pdf.setPage(pageNumber)
            pdf.setFont('helvetica', 'normal')
            pdf.setFontSize(8)
            pdf.setTextColor(102, 112, 133)
            pdf.text(`${pageNumber} of ${totalPages}`, pageWidth / 2, pageHeight - 6, { align: 'center' })
        }

        if (output === 'blob') return pdf.output('blob')

        const safeFileName = item.name.replace(/[<>:"/\\|?*]/g, '-').trim() || 'SWMS'
        pdf.save(`${safeFileName}.pdf`)
    }

    const capitaliseWords = value => value.replace(/\b[a-z]/g, letter => letter.toUpperCase())
    const capitaliseFirstWord = value => value.replace(/^(\s*)([a-z])/, (_, spaces, letter) => `${spaces}${letter.toUpperCase()}`)
    const textField = (label, field, type = 'text', transformValue) => (
        <FieldLabel className='documentationField'>
            <span>{label}</span>
            <UiInput type={type} value={form[field]} onChange={event => setField(field, transformValue ? transformValue(event.target.value) : event.target.value)} />
        </FieldLabel>
    )

    return <div className={embedded ? 'documentationEmbeddedRoot' : ''}>
        <LoadingPopup visible={savingForm} message='Saving form...' />
        {!embedded && <Header />}
        <div className={embedded ? 'documentationEmbeddedShell' : 'pageShell'}>
            {!embedded && <Sidebar />}
            <PageContent as="div" enabled={!embedded} className={embedded ? 'documentationPage documentationEmbeddedPage' : 'pageContent documentationPage'}>
                {!embedded && <PageHeader title={"Forms & Docs"} subtitle={<>Create and maintain workplace forms</>} />}
                <div className={embedded ? 'pageBody documentationEmbeddedBody' : 'pageBody'}>
                    {!embedded && <div className='documentationSelectorLayout'>
                        <SectionCard className='documentationFormTypePanel' title={<>Form Type</>}>

                            <Tabs className='documentationTypePills' role='tablist' aria-label='Documentation forms'>
                                {documentationTypes.map(type => (
                                    <Tabs.Tab active={selectedType === type}
                                        type='button'
                                        role='tab'
                                        aria-selected={selectedType === type}
                                        className={selectedType === type ? 'documentationTypePill documentationTypePillActive' : 'documentationTypePill'}
                                        onClick={() => {
                                            abandonLibraryEdit(null)
                                            setSelectedType(type)
                                            setSelectedSwmsType(null)
                                        }}
                                        key={type}
                                    >
                                        {type}
                                    </Tabs.Tab>
                                ))}
                            </Tabs>
                        </SectionCard>
                        {selectedType && <SectionCard className='documentationSubsectionPanel' title={<>{selectedType}</>}>

                            {selectedType === documentationTypes[0] ? (
                                <div className='documentationSubTypes'>
                                    {swmsTypes.map(swmsType => (
                                        <Button variant="secondary" size="md"
                                            type='button'
                                            className={selectedSwmsType === swmsType ? 'documentationSubType documentationSubTypeActive' : 'documentationSubType'}
                                            onClick={() => {
                                                abandonLibraryEdit(swmsType)
                                                setSelectedSwmsType(swmsType)
                                                ensureSwmsNumber(swmsType)
                                            }}
                                            key={swmsType}
                                        >
                                            {swmsType}
                                        </Button>
                                    ))}
                                </div>
                            ) : <p>No forms yet.</p>}
                        </SectionCard>}
                        <SectionCard className='documentationSubsectionPanel documentationLibraryPanel' title={<>Library</>}>

                            <div className='documentationLibraryTable'>
                                <div className='documentationLibraryHeader'>
                                    <span>Name</span>
                                    <span>Action</span>
                                </div>
                                {swmsLibrary.map(item => (
                                    <div className='documentationLibraryRow' key={item.id || `${item.category}-${item.savedAt}`}>
                                        <span>{item.name}</span>
                                        <div className='documentationLibraryActions'>
                                            <Button variant="secondary" size="md" type='button' onClick={() => setViewingLibraryItem(item)}>View</Button>
                                            <Button data-action-tone="edit" variant="secondary" size="md"  type='button' onClick={() => editLibraryItem(item)}>Edit</Button>
                                            <Button variant="secondary" size="md" type='button' className='documentationLibraryDownload' onClick={() => withDownloadProgress(() => downloadLibraryItem(item))}>Download</Button>
                                            <Button data-action-tone="delete" variant="danger" size="md"  type='button' className='documentationLibraryDelete' onClick={() => setDeletingLibraryItem(item)}>Delete</Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </SectionCard>
                    </div>}

                    {selectedType && (selectedType === 'Daily Vehicle Checklist' ? (
                        <DailyVehicleChecklist key={activeOrg?.id || 'none'} setSuccess={setSuccess} orgId={activeOrg?.id} />
                    ) : selectedType === 'Daily TGS Checklist' ? (
                        <DailyTgsChecklist key={activeOrg?.id || 'none'} setSuccess={setSuccess} orgId={activeOrg?.id} />
                    ) : selectedType === 'Job Safety Analysis' ? (
                        <JobSafetyAnalysis key={activeOrg?.id || 'none'} setSuccess={setSuccess} orgId={activeOrg?.id} />
                    ) : selectedType === 'Incident Report' ? (
                        <IncidentReport key={activeOrg?.id || 'none'} setSuccess={setSuccess} orgId={activeOrg?.id} />
                    ) : selectedType !== documentationTypes[0] || !['General Labour', 'Telehandler', 'Traffic Management', 'Elevated Work Platform (Over 11m)', 'Demolition'].includes(selectedSwmsType) ? (
                        <div className='documentationEmptyForm'>
                            <h2>
                                {selectedType === documentationTypes[0]
                                    ? <>{selectedType} - <em>select category</em></>
                                    : selectedType}
                            </h2>
                            <p>Select this form to view and complete its fields.</p>
                        </div>
                    ) : <>
                    {!hideFormHeading && <div className='documentationFormHeading'>
                        <div>
                            <h2>Safe Work Method Statement - {selectedSwmsType}</h2>
                            <p>Complete the fields below to prepare the SWMS.</p>
                        </div>
                        <span className='documentationDraftBadge'>Draft</span>
                    </div>}

                    {embedded && hideFormHeading && !isReadOnly && (
                        <div className='documentationSiteFormTopRow'>
                            <FieldLabel choice className='documentationAttachAllocationCheckbox'>
                                <UiCheckbox
                                    type='checkbox'
                                    checked={attachToAllocations}
                                    onChange={event => setAttachToAllocations(event.target.checked)}
                                />
                                <span>Add to allocations</span>
                            </FieldLabel>
                        </div>
                    )}

                    <fieldset className='documentationReadOnlyFieldset' disabled={isReadOnly}>
                    <div className='documentationFormStack'>
                        <Section title='SWMS Details and Approval' variant='card'>
                            <div className='documentationFieldGrid'>
                                <FieldLabel className='documentationField'><span>Business Name</span><UiInput value='Multicrew Pty Ltd' disabled /></FieldLabel>
                                <FieldLabel className='documentationField'><span>ABN</span><UiInput value='93668879294' disabled /></FieldLabel>
                                <FieldLabel className='documentationField'>
                                    <span>SWMS Number</span>
                                    <UiInput value={form.swmsNumber} disabled />
                                </FieldLabel>
                                <FieldLabel className='documentationField'><span>Business Address</span><UiInput value='Shop 2/40 Wollongong Rd, Arncliffe NSW 2205' disabled /></FieldLabel>
                                {textField('Approved By - Full Name *', 'approverName', 'text', capitaliseWords)}
                                {textField('Position *', 'approverTitle', 'text', capitaliseWords)}
                                <div className='documentationField'><MyCalendar showDateOnly noMaxDate startDate={form.approvalDate} setStartDate={value => setField('approvalDate', value)} label='Approval Date *' /></div>
                            </div>
                            {!approvalDetailsComplete && <p className='documentationRequiredNotice'>Complete all required fields to continue to the next section.</p>}
                        </Section>

                        {approvalDetailsComplete && <>
                        <Section title='Project and Principal Contractor Details' variant='card'>
                            <div className='documentationFieldGrid'>
                                <div className='documentationField documentationSearchField'>
                                    <Dropdown
                                        label='Client *'
                                        placeholder='Search clients'
                                        options={clientOptions}
                                        value={clientOptions.find((option) => option.value === selectedClientId) || null}
                                        setValue={selectClient}
                                    />
                                </div>
                                <div className='documentationField documentationSearchField'>
                                    <Dropdown
                                        label='Project Name *'
                                        placeholder={selectedClientId ? 'Search sites' : 'Select a client first'}
                                        disabled={!selectedClientId}
                                        options={siteOptions}
                                        value={siteOptions.find((option) => option.value === selectedSiteId) || null}
                                        setValue={selectSite}
                                    />
                                </div>
                                {textField('Project Address *', 'projectAddress', 'text', capitaliseWords)}
                                {textField('Site Manager *', 'manager', 'text', capitaliseWords)}
                                {textField('Contact Number *', 'contactPhone', 'tel')}
                                <div className='documentationField'><MyCalendar showDateOnly noMaxDate startDate={form.suppliedDate} setStartDate={value => setField('suppliedDate', value)} label='Date SWMS Supplied to Manager *' /></div>
                            </div>
                            {!projectDetailsComplete && <p className='documentationRequiredNotice'>Complete all required fields to continue to the next section.</p>}
                        </Section>

                        {projectDetailsComplete && <>
                        <Section title='Personnel Consulted and Communicated' variant='card'>
                            <div className='documentationEditableTable'>
                                <div className='documentationTableHeader'><span>Full Name *</span><span>Position *</span><span>Signature *</span><span>Date *</span><span>Action</span></div>
                                {form.consultation.map((person, index) => (
                                    <div className='documentationTableRow' key={`consultation-${index}`}>
                                        <UiInput value={person.name} disabled={person.autoPopulated} onChange={event => updateRow('consultation', index, 'name', event.target.value)} />
                                        <UiInput value={person.position} disabled={person.autoPopulated} onChange={event => updateRow('consultation', index, 'position', event.target.value)} />
                                        <div className='documentationSignatureCell'>
                                            {person.signature ? <img src={person.signature} alt={`Signature for ${person.name || `person ${index + 1}`}`} /> : null}
                                            <Button variant="secondary" size="md"
                                                type='button'
                                                className='secondaryButton documentationSignButton'
                                                onClick={() => {
                                                    setSignaturePersonIndex(index)
                                                    setSignatureHasInk(false)
                                                }}
                                            >
                                                {person.signature ? 'Sign Again' : 'Sign'}
                                            </Button>
                                        </div>
                                        <div className='documentationTableDatePicker'>
                                            <MyCalendar
                                                hideLabel
                                                showDateOnly
                                                noMaxDate
                                                startDate={person.date}
                                                setStartDate={value => updateRow('consultation', index, 'date', value ? value.toISOString() : '')}
                                                placeholder='Select date'
                                            />
                                        </div>
                                        <Button data-action-tone="delete" variant="danger" size="md"  type='button' className='textButton textButtonDanger' onClick={() => removeConsultationPerson(index)}>Delete</Button>
                                    </div>
                                ))}
                            </div>
                            <Button variant="create" size="md" type='button' className='documentationAddRowButton' onClick={() => addRow('consultation', emptyPerson())}><PlusIcon />Add Person</Button>
                            {!consultationComplete && <p className='documentationRequiredNotice'>Add and complete at least one person to continue to the next section.</p>}
                        </Section>

                        {consultationComplete && <>
                        <Section title='High-Risk Construction Work' variant='card'>
                            <div className='documentationCheckGrid'>
                                {highRiskOptions.map(option => <FieldLabel choice key={option} className={form.highRisk.includes(option) ? 'documentationCheck documentationCheckActive' : 'documentationCheck'}>
                                    <UiCheckbox type='checkbox' checked={form.highRisk.includes(option)} onChange={() => toggleOption('highRisk', option)} />
                                    <span>{option}</span>
                                </FieldLabel>)}
                            </div>
                            {!highRiskComplete && <p className='documentationRequiredNotice'>Select at least one option to continue to the next section.</p>}
                        </Section>

                        {highRiskComplete && <>
                        <Section title='High-Risk Machinery or Equipment Nearby' variant='card'>
                            <div className='documentationCheckGrid documentationCheckGridCompact'>
                                {machineryOptions.map(option => <FieldLabel choice key={option} className={form.machinery.includes(option) ? 'documentationCheck documentationCheckActive' : 'documentationCheck'}>
                                    <UiCheckbox type='checkbox' checked={form.machinery.includes(option)} onChange={() => toggleMachineryOption(option)} />
                                    <span>{option}</span>
                                </FieldLabel>)}
                            </div>
                            <div className='documentationOtherField'>{textField('Other Machinery / Equipment', 'otherMachinery')}</div>
                            {!machineryComplete && <p className='documentationRequiredNotice'>Select at least one option to continue to the next section.</p>}
                        </Section>

                        {machineryComplete && <>
                        <Section title='Personal Protective Equipment (PPE)' variant='card'>
                            <div className='documentationCheckGrid documentationCheckGridCompact'>
                                {ppeOptions.map(option => <FieldLabel choice key={option} className={form.ppe.includes(option) ? 'documentationCheck documentationCheckActive' : 'documentationCheck'}>
                                    <UiCheckbox type='checkbox' checked={form.ppe.includes(option)} onChange={() => toggleOption('ppe', option)} />
                                    <span>{option}</span>
                                </FieldLabel>)}
                            </div>
                            {!ppeComplete && <p className='documentationRequiredNotice'>Select at least one option to continue to the next section.</p>}
                        </Section>

                        {ppeComplete && <>
                        <Section title='Job Steps, Hazards and Controls' variant='card'>
                            <div className='documentationHazardList'>
                                {form.hazards.map((hazard, index) => <div className='documentationHazardCard' key={`hazard-${index}`}>
                                    <div className='documentationHazardCardHeader'>
                                        <strong>Risk Item {index + 1}</strong>
                                    </div>
                                    <div className='documentationFieldGrid'>
                                        {['jobStep', 'hazards', 'initialRisk', 'residualRisk', 'responsible'].map(key => {
                                            const isRiskField = key === 'initialRisk' || key === 'residualRisk'
                                            const isLocked = (!hazard.custom && index < presetHazardCount) || hazard.saved
                                            return (
                                                <FieldLabel className='documentationField' key={key}>
                                                    <span>{{ jobStep: 'Job Step', hazards: 'Potential Hazards', initialRisk: 'Initial Risk', residualRisk: 'Residual Risk', responsible: 'Responsible Person' }[key]}</span>
                                                    {isRiskField ? (
                                                        <UiSelect value={hazard[key]} disabled={isLocked} onChange={event => updateRow('hazards', index, key, event.target.value)}>
                                                            <option value=''>Select</option>
                                                            <option value='Low'>Low</option>
                                                            <option value='Moderate'>Moderate</option>
                                                            <option value='High'>High</option>
                                                            <option value='Acute'>Acute</option>
                                                        </UiSelect>
                                                    ) : (
                                                        <UiInput value={hazard[key]} disabled={isLocked} onChange={event => updateRow('hazards', index, key, capitaliseFirstWord(event.target.value))} />
                                                    )}
                                                </FieldLabel>
                                            )
                                        })}
                                        <FieldLabel className='documentationField documentationFieldWide'><span>Control Measures</span><Textarea value={hazard.controls} disabled={(!hazard.custom && index < presetHazardCount) || hazard.saved} onChange={event => updateRow('hazards', index, 'controls', capitaliseFirstWord(event.target.value))} /></FieldLabel>
                                    </div>
                                    {(hazard.custom || index >= presetHazardCount) && (
                                        <div className='documentationHazardCardActions'>
                                            <Button data-action-tone="delete" variant="danger" size="md"
                                                type='button'
                                                className='documentationHazardDeleteButton'
                                                onClick={() => setRiskItemConfirmation({
                                                    title: 'Delete Risk Item?',
                                                    message: 'Are you sure you want to delete this Risk Item?',
                                                    confirmLabel: 'Delete',
                                                    danger: true,
                                                    action: () => removeRow('hazards', index),
                                                })}
                                            >
                                                Delete
                                            </Button>
                                            {hazard.saved ? (
                                                <Button data-action-tone="edit" variant="secondary" size="md"
                                                    type='button'
                                                    className='documentationHazardEditButton'
                                                    onClick={() => updateRow('hazards', index, 'saved', false)}
                                                >
                                                    Edit
                                                </Button>
                                            ) : (
                                                <Button data-action-tone="save" variant="soft" size="md"
                                                    type='button'
                                                    className='documentationHazardSaveButton'
                                                    disabled={!isRiskItemComplete(hazard)}
                                                    onClick={() => setRiskItemConfirmation({
                                                        title: 'Save Risk Item?',
                                                        message: 'Save this Risk Item and close its fields for editing?',
                                                        confirmLabel: 'Save',
                                                        action: () => updateRow('hazards', index, 'saved', true),
                                                    })}
                                                >
                                                    Save
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>)}
                            </div>
                            <Button variant="create" size="md" type='button' className='documentationAddRowButton' onClick={() => addRow('hazards', { jobStep: '', hazards: '', initialRisk: '', controls: '', residualRisk: '', responsible: '', custom: true, saved: false })}><PlusIcon />Add Risk Item</Button>
                            {!additionalRiskItemsComplete && <p className='documentationRequiredNotice'>Complete all fields in each additional Risk Item to continue to the next section.</p>}
                        </Section>

                        {additionalRiskItemsComplete && <>
                        <Section title='Signatories of the Safe Work Method Statement' variant='card'>
                            <div className='documentationEditableTable'>
                                <div className='documentationTableHeader'><span>Full Name *</span><span>Position *</span><span>Signature *</span><span>Date *</span><span>Action</span></div>
                                {form.signatories.map((person, index) => <div className='documentationTableRow' key={`signatory-${index}`}>
                                    <UiInput value={person.name} onChange={event => updateRow('signatories', index, 'name', event.target.value)} />
                                    <UiInput value={person.position} onChange={event => updateRow('signatories', index, 'position', event.target.value)} />
                                    <div className='documentationSignatureCell'>
                                        {person.signature ? <img src={person.signature} alt={`Signature for ${person.name || `signatory ${index + 1}`}`} /> : null}
                                        <Button variant="secondary" size="md"
                                            type='button'
                                            className='secondaryButton documentationSignButton'
                                            onClick={() => {
                                                setSignatureSignatoryIndex(index)
                                                setSignatureHasInk(false)
                                            }}
                                        >
                                            {person.signature ? 'Sign Again' : 'Sign'}
                                        </Button>
                                    </div>
                                    <div className='documentationTableDatePicker'>
                                        <MyCalendar
                                            hideLabel
                                            showDateOnly
                                            noMaxDate
                                            startDate={person.date}
                                            setStartDate={value => updateRow('signatories', index, 'date', value ? value.toISOString() : '')}
                                            placeholder='Select date'
                                        />
                                    </div>
                                    <Button data-action-tone="delete" variant="danger" size="md"  type='button' className='textButton textButtonDanger' onClick={() => removeRow('signatories', index)}>Delete</Button>
                                </div>)}
                            </div>
                            <Button variant="create" size="md" type='button' className='documentationAddRowButton' onClick={() => addRow('signatories', emptyPerson())}><PlusIcon />Add Signatory</Button>
                            {!signatoriesComplete && <p className='documentationRequiredNotice'>Complete all fields for every signatory to continue to the next section.</p>}
                        </Section>

                        {signatoriesComplete && <>
                        <Section title='SWMS Monitoring and Review' variant='card'>
                            <div className='documentationReviewList'>
                                {form.reviews.map((review, index) => <div className='documentationReviewCard' key={`review-${index}`}>
                                    <div className='documentationFieldGrid'>
                                        <FieldLabel className='documentationField'>
                                            <span>Review Number</span>
                                            <UiInput value={review.reviewNumber || String(index + 1)} disabled />
                                        </FieldLabel>
                                        <FieldLabel className='documentationField'>
                                            <span>Full Name</span>
                                            <UiInput value={review.name} onChange={event => updateRow('reviews', index, 'name', event.target.value)} />
                                        </FieldLabel>
                                        <FieldLabel className='documentationField'>
                                            <span>Date</span>
                                            <DateInput type='date' value={review.date} onChange={event => updateRow('reviews', index, 'date', event.target.value)} />
                                        </FieldLabel>
                                        <FieldLabel className='documentationField documentationFieldWide'><span>Feedback / Comments</span><Textarea value={review.comments} onChange={event => updateRow('reviews', index, 'comments', event.target.value)} /></FieldLabel>
                                    </div>
                                    <Button data-action-tone="delete" variant="danger" size="md"  type='button' className='textButton textButtonDanger' onClick={() => removeRow('reviews', index)}>Delete Review</Button>
                                </div>)}
                            </div>
                            <Button variant="create" size="md" type='button' className='documentationAddRowButton' onClick={addReviewRow}><PlusIcon />Add Review</Button>
                        </Section>
                        </>}
                        </>}
                        </>}
                        </>}
                        </>}
                        </>}
                        </>}
                        </>}
                    </div>
                    </fieldset>

                    {(isReadOnly || (approvalDetailsComplete && projectDetailsComplete && consultationComplete && highRiskComplete && machineryComplete && ppeComplete && additionalRiskItemsComplete && signatoriesComplete)) && <div className='documentationStickyActions'>
                        {isReadOnly ? (
                            <>
                                <Button variant="secondary" size="md"
                                    type='button'
                                    className='documentationFormCancelButton'
                                    onClick={() => onCancel?.()}
                                >
                                    Cancel
                                </Button>
                                <Button data-action-tone="edit" variant="secondary" size="md"  type='button' className='actionWithIcon' onClick={() => setIsReadOnly(false)}>
                                    Edit
                                </Button>
                            </>
                        ) : editingLibraryItem ? (
                            <Button data-action-tone="cancel" variant="secondary" size="md"  type='button' className='documentationEditCancelButton' onClick={cancelLibraryEdit}>Cancel</Button>
                        ) : (
                            <Button data-action-tone="cancel" variant="secondary" size="md"
                                type='button'
                                className='documentationFormCancelButton'
                                onClick={() => {
                                    if (onCancel) {
                                        onCancel()
                                    } else {
                                        setSelectedSwmsType(null)
                                    }
                                }}
                            >
                                Cancel
                            </Button>
                        )}
                        {!isReadOnly && <Button data-action-tone="save" variant="soft" size="md"
                            type='button'
                            className='actionWithIcon'
                            onClick={() => {
                                if (embedded) {
                                    saveEmbeddedDraft()
                                    return
                                }
                                saveStandaloneDraft()
                            }}
                            disabled={savingForm}
                        >
                            {savingForm ? 'Saving...' : 'Save Form'}
                        </Button>}
                    </div>}
                    </>)}
                </div>
            </PageContent>
        </div>
        <ModalComponent
            showModal={signaturePersonIndex !== null || signatureSignatoryIndex !== null}
            setShowModal={() => {
                setSignaturePersonIndex(null)
                setSignatureSignatoryIndex(null)
                setSignatureHasInk(false)
            }}
            customMaxWidth='560px'
            customContentStyle={{ minWidth: 0, width: 'min(560px, calc(100vw - 32px))' }}
        >
            <div className='documentationSignatureModal'>
                <h2 className='modalTitle'>Add Signature</h2>
                <p className='modalSubtitle'>Sign inside the box below.</p>
                <SignatureCanvas
                    ref={signatureCanvasRef}
                    penColor='black'
                    onEnd={() => setSignatureHasInk(true)}
                    canvasProps={{ width: 500, height: 220, className: 'documentationSignatureCanvas' }}
                />
                <div className='modalFooter'>
                    <Button variant="secondary" size="md"
                        type='button'
                        className='secondaryButton'
                        onClick={() => {
                            signatureCanvasRef.current?.clear()
                            setSignatureHasInk(false)
                        }}
                    >
                        Clear
                    </Button>
                    <Button data-action-tone="cancel" variant="secondary" size="md"
                        type='button'
                        className='secondaryButton'
                        onClick={() => {
                            setSignaturePersonIndex(null)
                            setSignatureSignatoryIndex(null)
                            setSignatureHasInk(false)
                        }}
                    >
                        Cancel
                    </Button>
                    <Button data-action-tone="save" variant="soft" size="md"
                        type='button'
                        disabled={!signatureHasInk}
                        onClick={() => {
                            const signature = signatureCanvasRef.current?.getTrimmedCanvas().toDataURL('image/png')
                            if (signaturePersonIndex !== null && signature) {
                                updateRow('consultation', signaturePersonIndex, 'signature', signature)
                            }
                            if (signatureSignatoryIndex !== null && signature) {
                                updateRow('signatories', signatureSignatoryIndex, 'signature', signature)
                            }
                            setSignaturePersonIndex(null)
                            setSignatureSignatoryIndex(null)
                            setSignatureHasInk(false)
                        }}
                    >
                        Save Signature
                    </Button>
                </div>
            </div>
        </ModalComponent>
        <ModalComponent
            showModal={!!riskItemConfirmation}
            setShowModal={setRiskItemConfirmation}
            customMaxWidth='300px'
            customContentStyle={{ minWidth: 0, width: 'min(300px, calc(100vw - 32px))', padding: '14px' }}
        >
            <div className='allocationConfirmMessage'>
                <h2 className='modalTitle'>{riskItemConfirmation?.title}</h2>
                <p className='modalSubtitle'>{riskItemConfirmation?.message}</p>
                <div className='modalFooter'>
                    <Button data-action-tone="cancel" variant="secondary" size="md"  type='button' className='secondaryButton' onClick={() => setRiskItemConfirmation(null)}>Cancel</Button>
                    <Button variant="secondary" size="md"
                        type='button'
                        className={riskItemConfirmation?.danger ? 'modalDangerButton' : ''}
                        onClick={() => {
                            riskItemConfirmation?.action?.()
                            setRiskItemConfirmation(null)
                        }}
                    >
                        {riskItemConfirmation?.confirmLabel}
                    </Button>
                </div>
            </div>
        </ModalComponent>
        <ModalComponent
            showModal={showSaveFileModal}
            setShowModal={setShowSaveFileModal}
            customMaxWidth='420px'
            customContentStyle={{ minWidth: 0, width: 'min(420px, calc(100vw - 32px))' }}
        >
            <div>
                <h2 className='modalTitle'>Name This File</h2>
                <p className='modalSubtitle'>Enter a name for this Safe Work Method Statement.</p>
                <FieldLabel className='documentationField documentationSaveFileField'>
                    <span>File Name *</span>
                    <UiInput
                        autoFocus
                        value={saveFileName}
                        onChange={event => setSaveFileName(event.target.value)}
                        onKeyDown={event => {
                            if (event.key === 'Enter' && saveFileName.trim()) saveDraft()
                        }}
                    />
                </FieldLabel>
                <div className='modalFooter'>
                    <Button data-action-tone="cancel" variant="secondary" size="md"  type='button' className='secondaryButton' onClick={() => setShowSaveFileModal(false)}>Cancel</Button>
                    <Button data-action-tone="save" variant="soft" size="md"  type='button' disabled={!saveFileName.trim() || savingForm} onClick={saveDraft}>{savingForm ? 'Saving...' : 'Save'}</Button>
                </div>
            </div>
        </ModalComponent>
        <ModalComponent
            showModal={!!viewingLibraryItem}
            setShowModal={setViewingLibraryItem}
            customMaxWidth='900px'
            customContentStyle={{ minWidth: 0, width: 'min(900px, calc(100vw - 32px))', paddingBottom: 0 }}
        >
            <div className='documentationLibraryViewModal'>
                <div className='documentationLibraryViewHeader'>
                    <div>
                        <span className='documentationLibraryViewEyebrow'>Safe Work Method Statement</span>
                        <h2 className='modalTitle'>{viewingLibraryItem?.name}</h2>
                        <p>Read-only document preview</p>
                    </div>
                    <span className='documentationLibraryViewBadge'>{viewingLibraryItem?.category}</span>
                </div>
                <div className='documentationLibraryViewDetails'>
                    <div><span>Category</span><strong>{viewingLibraryItem?.category}</strong></div>
                    <div><span>SWMS Number</span><strong>{viewingLibraryItem?.form?.swmsNumber || '—'}</strong></div>
                    <div>
                        <span>Saved</span>
                        <strong className='allocationPreWrapCell'>{viewingLibraryItem?.savedAt ? formatDisplayDateTime(viewingLibraryItem.savedAt) : '—'}</strong>
                    </div>
                </div>
                {viewingLibraryItem?.form && <>
                    <div className='documentationLibraryViewSection documentationLibraryViewSectionCompact'>
                        <h3>SWMS Details and Approval</h3>
                        <div className='documentationLibraryViewGrid'>
                            <div><span>Business Name</span><strong>Multicrew Pty Ltd</strong></div>
                            <div><span>ABN</span><strong>93668879294</strong></div>
                            <div><span>Approved By – Full Name</span><strong>{formatName(viewingLibraryItem.form.approverName || '—')}</strong></div>
                            <div><span>Position</span><strong>{viewingLibraryItem.form.approverTitle || '—'}</strong></div>
                            <div><span>Approval Date</span><strong>{viewingLibraryItem.form.approvalDate ? formatDisplayDate(viewingLibraryItem.form.approvalDate) : '—'}</strong></div>
                        </div>
                    </div>
                    <div className='documentationLibraryViewSection documentationLibraryViewSectionCompact'>
                        <h3>Project and Principal Contractor Details</h3>
                        <div className='documentationLibraryViewGrid'>
                            {[
                                ['Client', viewingLibraryItem.form.client],
                                ['Project Name', viewingLibraryItem.form.projectName],
                                ['Project Address', viewingLibraryItem.form.projectAddress],
                                ['Site Manager', viewingLibraryItem.form.manager],
                                ['Contact Number', viewingLibraryItem.form.contactPhone],
                            ].map(([label, value]) => <div key={label}><span>{label}</span><strong>{value || '—'}</strong></div>)}
                        </div>
                    </div>
                    {[
                        ['Personnel Consulted and Communicated', viewingLibraryItem.form.consultation],
                        ['Signatories of the Safe Work Method Statement', viewingLibraryItem.form.signatories],
                    ].map(([title, people]) => (
                        <div className='documentationLibraryViewSection documentationLibraryPeopleSection' key={title}>
                            <h3>{title}</h3>
                            <div className='documentationLibraryPeopleTable'>
                                <div className='documentationLibraryPeopleHeader'>
                                    <span>Full Name</span><span>Position</span><span>Signature</span><span>Date</span>
                                </div>
                                {(people || []).map((person, index) => (
                                    <div className='documentationLibraryPeopleRow' key={`${title}-${index}`}>
                                        <strong>{person.name || '—'}</strong>
                                        <span>{person.position || '—'}</span>
                                        <div>{person.signature?.startsWith('data:image') ? <img src={person.signature} alt={`Signature for ${person.name || `person ${index + 1}`}`} /> : <span>—</span>}</div>
                                        <span>{person.date ? formatDisplayDate(person.date) : '—'}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                    <div className='documentationLibraryViewSection'>
                        <h3>Selections</h3>
                        <div className='documentationLibraryViewGrid'>
                            <div><span>High-Risk Construction Work</span><strong>{viewingLibraryItem.form.highRisk?.join(', ') || '—'}</strong></div>
                            <div><span>Machinery / Equipment</span><strong>{viewingLibraryItem.form.machinery?.join(', ') || '—'}</strong></div>
                            <div><span>PPE</span><strong>{viewingLibraryItem.form.ppe?.join(', ') || '—'}</strong></div>
                        </div>
                    </div>
                    <div className='documentationLibraryViewSection'>
                        <h3>Job Steps, Hazards and Controls</h3>
                        <div className='documentationLibraryRiskList'>
                            {viewingLibraryItem.form.hazards?.map((hazard, index) => (
                                <div key={`view-risk-${index}`}>
                                    <strong>Risk Item {index + 1}: {hazard.jobStep}</strong>
                                    <span>{hazard.hazards}</span>
                                    <span>Initial: {hazard.initialRisk} · Residual: {hazard.residualRisk}</span>
                                    <span>{hazard.controls}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </>}
                <div className='modalFooter modalFooterSticky documentationLibraryViewFooter'>
                    <Button variant="secondary" size="md" type='button' onClick={() => setViewingLibraryItem(null)}>Cancel</Button>
                </div>
            </div>
        </ModalComponent>
        <ModalComponent
            showModal={!!deletingLibraryItem}
            setShowModal={setDeletingLibraryItem}
            customMaxWidth='320px'
            customContentStyle={{ minWidth: 0, width: 'min(320px, calc(100vw - 32px))', padding: '14px' }}
        >
            <div className='allocationConfirmMessage'>
                <h2 className='modalTitle'>Delete File?</h2>
                <p className='modalSubtitle'>Are you sure you want to delete {deletingLibraryItem?.name} from the Library?</p>
                <div className='modalFooter'>
                    <Button data-action-tone="cancel" variant="secondary" size="md"  type='button' className='secondaryButton' onClick={() => setDeletingLibraryItem(null)}>Cancel</Button>
                    <Button data-action-tone="delete" variant="danger" size="md"  type='button' className='modalDangerButton' onClick={() => deleteLibraryItem(deletingLibraryItem)}>Delete</Button>
                </div>
            </div>
        </ModalComponent>
        <CompactSuccessModal success={success} setSuccess={setSuccess} />
    </div>
}

export default Documentation
